// components/AgencyAdminPanel.jsx
import { useState, useEffect } from 'react';
import { supabase } from '../utils/supabaseClient';
import { STAGES, fmtMoney } from '../utils/constants';
import { agencyDb, templatesDb } from '../utils/store';
import { APP_DEFAULT_PROFILE, mergeIntoDefault } from '../utils/docTemplate';

const ROLE_LABELS = { member: 'Analyst', admin: 'Supervisor', agency_super_admin: 'Division Chief' };

// ── Default Term-clause sub-letters ─────────────────────────────────────
// Mirrors the in-app fallback used by the docx builder when no agency-specific
// term_clause is set. Two boilerplate sub-letters: commencement (A) and
// amendment (B). Each uses placeholder tokens that the docx builder
// substitutes at draft time: {start_date}, {end_date}, {approver},
// {agency_short_name}, {contract_title}, {contractor_name}.
const DEFAULT_TERM_SUBLETTERS = [
  {
    id: 'commencement',
    text: 'The term of this Contract will commence on {start_date}, or upon approval by {approver}, whichever is later, and will continue through {end_date}.',
    source: 'default',
    active: true,
  },
  {
    id: 'amendment',
    text: 'Any changes to this Contract will require consent of both parties and may require an amendment to be approved by {approver}.',
    source: 'default',
    active: true,
  },
];

export default function AgencyAdminPanel({ profile, contracts: allContracts, onClose, toast, apiKey, callAI, templates = [], uploadToAnthropicFiles }) {
  const [tab, setTab] = useState('pending'); // pending | myteam | allteams | doctemplate
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState({});
  const [pendingCount, setPendingCount] = useState(0);
  const [reassignTarget, setReassignTarget] = useState(null); // contract being reassigned
  const [analysts, setAnalysts] = useState([]); // all agency analysts for reassign dropdown
  const [userAction, setUserAction] = useState(null); // { user, action: 'role'|'team'|'deactivate'|'reinstate' }
  const [supervisors, setSupervisors] = useState([]); // all supervisors for team change

  // Doc template profile state
  const [docProfile, setDocProfile] = useState(null);
  const [docProfileLoading, setDocProfileLoading] = useState(false);
  const [docProfileSaving, setDocProfileSaving] = useState(false);
  const [docProfileForm, setDocProfileForm] = useState({
    fontFamily: 'Arial', bodySizePt: 12, tableSizePt: 10,
    agencyNameInText: '', spacingPt: 6,
    preferredPhrases: [], avoidPhrases: [],
  });
  const [showAdvancedJson, setShowAdvancedJson] = useState(false);
  const [advancedJson, setAdvancedJson] = useState('');
  const [advancedJsonError, setAdvancedJsonError] = useState('');

  // ── PAAL document state ──────────────────────────────────────────────
  const [paalMeta, setPaalMeta] = useState(null);
  const [paalValues, setPaalValues] = useState(null);
  const [paalExtracting, setPaalExtracting] = useState(false);
  const [paalPreview, setPaalPreview] = useState(null);
  const [paalSaving, setPaalSaving] = useState(false);

  // ── Term-clause review state ─────────────────────────────────────────
  // termClause: the saved structured config { subletters, last_reviewed_at, ... }.
  // termReviewOpen: whether the review modal is showing.
  // termReviewLoading: Penny is analyzing.
  // termFindings: array of finding objects Penny returned (each with id, type, etc.).
  // termFindingsChecked: which finding IDs the admin has checked to apply.
  const [termClause, setTermClause] = useState(null);
  const [termReviewOpen, setTermReviewOpen] = useState(false);
  const [termReviewLoading, setTermReviewLoading] = useState(false);
  const [termFindings, setTermFindings] = useState([]);
  const [termFindingsChecked, setTermFindingsChecked] = useState({});
  const [termApplying, setTermApplying] = useState(false);
  // termSubletters: the editable working copy of subletters shown in the
  // "current state" view in the Doc Profile tab.
  const [termSubletters, setTermSubletters] = useState([]);
  const [termEditing, setTermEditing] = useState(null); // { idx } currently being edited

  // ── Exhibit B template state ─────────────────────────────────────────
  // exhibitBTemplate: the saved structured config { sections, last_reviewed_at, ... }
  // exbSections: editable working copy of sections (the heading+text blocks)
  // exbReviewOpen: whether the review/extract modal is open
  // exbReviewLoading: Penny is extracting from templates
  // exbExtracted: the sections Penny extracted (preview form data, pre-save)
  // exbSaving: writing to doc_template.exhibit_b_template
  // exbEditing: { idx } if a section is being edited inline
  const [exhibitBTemplate, setExhibitBTemplate] = useState(null);
  const [exbSections, setExbSections] = useState([]);
  const [exbReviewOpen, setExbReviewOpen] = useState(false);
  const [exbReviewLoading, setExbReviewLoading] = useState(false);
  const [exbExtracted, setExbExtracted] = useState(null);
  const [exbSaving, setExbSaving] = useState(false);
  const [exbEditing, setExbEditing] = useState(null);

  // ── Exhibit D template state ─────────────────────────────────────────
  // Mirrors Exhibit B exactly — agency-level Special Terms and Conditions
  // baseline that the docx builder uses, with Penny customizing per-contract.
  const [exhibitDTemplate, setExhibitDTemplate] = useState(null);
  const [exdSections, setExdSections] = useState([]);
  const [exdReviewOpen, setExdReviewOpen] = useState(false);
  const [exdReviewLoading, setExdReviewLoading] = useState(false);
  const [exdExtracted, setExdExtracted] = useState(null);
  const [exdSaving, setExdSaving] = useState(false);
  const [exdEditing, setExdEditing] = useState(null);

  // ── Clause Library state ─────────────────────────────────────────────
  // clauseLibrary: { A: [...], B: [...], D: [...], Sol: [...] }
  // Each clause: { id, sectionName, language, locked, required, sortOrder }
  const [clauseLibrary, setClauseLibrary] = useState({ A: [], B: [], D: [], Sol: [] });
  const [clauseTab, setClauseTab] = useState('A');
  const [clauseSaving, setClauseSaving] = useState(false);
  const [clauseEditing, setClauseEditing] = useState(null); // { exhibit, idx } or null
  const [clauseEditForm, setClauseEditForm] = useState({ sectionName: '', language: '', locked: true, required: false });
  const [clauseAdding, setClauseAdding] = useState(null); // exhibit key or null

  const isAgencySuperAdmin = profile.role === 'agency_super_admin';
  const isAgencyAdmin = profile.role === 'admin' || isAgencySuperAdmin;
  const currentUserId = profile.id;

  useEffect(() => { loadUsers(); loadPendingCount(); }, []); // eslint-disable-line
  useEffect(() => { if (tab !== 'pending') loadUsers(); else loadPending(); }, [tab]); // eslint-disable-line
  useEffect(() => { if (tab === 'doctemplate' || tab === 'clauses') loadDocTemplate(); }, [tab]); // eslint-disable-line

  // Load the agency's doc_template profile and seed the form fields
  async function loadDocTemplate() {
    setDocProfileLoading(true);
    try {
      // We need agency_id — fetch via user_profiles since profile.agencyName isn't a UUID
      const { data: prof } = await supabase.from('user_profiles')
        .select('agency_id').eq('id', currentUserId).single();
      const agencyId = prof?.agency_id;
      if (!agencyId) {
        setDocProfile(null);
        setDocProfileLoading(false);
        return;
      }
      const dt = await agencyDb.getDocTemplate(agencyId);
      const merged = mergeIntoDefault(dt || {});
      setDocProfile({ agencyId, raw: dt, merged });
      setDocProfileForm({
        fontFamily: merged.font?.family || 'Arial',
        bodySizePt: merged.font?.body_size_pt || 12,
        tableSizePt: merged.font?.table_size_pt || 10,
        agencyNameInText: merged.drafting_voice?.agency_name_in_text || '',
        spacingPt: merged.spacing?.paragraph_after_pt || 6,
        preferredPhrases: Array.isArray(merged.drafting_voice?.preferred_phrases) ? merged.drafting_voice.preferred_phrases : [],
        avoidPhrases: Array.isArray(merged.drafting_voice?.avoid_phrases) ? merged.drafting_voice.avoid_phrases : [],
      });
      setAdvancedJson(dt ? JSON.stringify(dt, null, 2) : '');

      // Load PAAL state — saved thresholds (if any) and file metadata
      setPaalValues(dt?.paal || null);
      const meta = await agencyDb.getPaalMeta(agencyId);
      setPaalMeta(meta);

      // Load term-clause state — fall back to the built-in defaults if not yet
      // configured. The defaults match what the docx builder uses when
      // doc_template.term_clause is absent.
      const tc = dt?.term_clause || null;
      setTermClause(tc);
      setTermSubletters(
        Array.isArray(tc?.subletters) && tc.subletters.length > 0
          ? tc.subletters
          : DEFAULT_TERM_SUBLETTERS
      );

      // Load Clause Library — agency-defined locked/reference clauses per exhibit
      const cl = dt?.clause_library || { A: [], B: [], D: [], Sol: [] };
      setClauseLibrary({
        A:   Array.isArray(cl.A)   ? cl.A   : [],
        B:   Array.isArray(cl.B)   ? cl.B   : [],
        D:   Array.isArray(cl.D)   ? cl.D   : [],
        Sol: Array.isArray(cl.Sol) ? cl.Sol : [],
      });
      const exbt = dt?.exhibit_b_template || null;
      setExhibitBTemplate(exbt);
      setExbSections(
        Array.isArray(exbt?.sections) ? exbt.sections : []
      );

      // Load Exhibit D template state — same pattern as Exhibit B.
      const exdt = dt?.exhibit_d_template || null;
      setExhibitDTemplate(exdt);
      setExdSections(
        Array.isArray(exdt?.sections) ? exdt.sections : []
      );
    } catch (e) {
      console.error('loadDocTemplate:', e);
      toast?.error?.('Could not load document template');
    } finally {
      setDocProfileLoading(false);
    }
  }

  async function saveDocTemplateForm() {
    if (!docProfile?.agencyId) { toast?.error?.('No agency context'); return; }
    setDocProfileSaving(true);
    try {
      const partial = {
        font: {
          family: docProfileForm.fontFamily,
          body_size_pt: Number(docProfileForm.bodySizePt) || 12,
          table_size_pt: Number(docProfileForm.tableSizePt) || 10,
        },
        spacing: {
          paragraph_after_pt: Number(docProfileForm.spacingPt) || 6,
        },
        drafting_voice: {
          agency_name_in_text: docProfileForm.agencyNameInText || profile.agencyName || 'the Agency',
          preferred_phrases: Array.isArray(docProfileForm.preferredPhrases) ? docProfileForm.preferredPhrases : [],
          avoid_phrases: Array.isArray(docProfileForm.avoidPhrases) ? docProfileForm.avoidPhrases : [],
        },
      };
      // Merge with any existing raw fields the user set in advanced mode
      const existing = docProfile.raw || {};
      const newRaw = { ...existing, ...partial };
      // Deep-merge nested objects
      newRaw.font = { ...(existing.font || {}), ...partial.font };
      newRaw.spacing = { ...(existing.spacing || {}), ...partial.spacing };
      newRaw.drafting_voice = { ...(existing.drafting_voice || {}), ...partial.drafting_voice };

      const ok = await agencyDb.saveDocTemplate(docProfile.agencyId, newRaw);
      if (ok) {
        toast?.success?.('Document profile saved');
        setDocProfile({ ...docProfile, raw: newRaw, merged: mergeIntoDefault(newRaw) });
        setAdvancedJson(JSON.stringify(newRaw, null, 2));
      } else {
        toast?.error?.('Save failed — you may not have permission');
      }
    } finally {
      setDocProfileSaving(false);
    }
  }

  async function saveAdvancedJson() {
    setAdvancedJsonError('');
    let parsed;
    try {
      parsed = advancedJson.trim() ? JSON.parse(advancedJson) : null;
    } catch (e) {
      setAdvancedJsonError('Invalid JSON: ' + e.message);
      return;
    }
    setDocProfileSaving(true);
    try {
      const ok = await agencyDb.saveDocTemplate(docProfile.agencyId, parsed);
      if (ok) {
        toast?.success?.('Profile saved');
        setDocProfile({ ...docProfile, raw: parsed, merged: mergeIntoDefault(parsed || {}) });
        // Re-seed the friendly form
        const merged = mergeIntoDefault(parsed || {});
        setDocProfileForm({
          fontFamily: merged.font?.family || 'Arial',
          bodySizePt: merged.font?.body_size_pt || 12,
          tableSizePt: merged.font?.table_size_pt || 10,
          agencyNameInText: merged.drafting_voice?.agency_name_in_text || '',
          spacingPt: merged.spacing?.paragraph_after_pt || 6,
          preferredPhrases: [],
          avoidPhrases: [],
        });
      } else {
        toast?.error?.('Save failed');
      }
    } finally {
      setDocProfileSaving(false);
    }
  }

  async function clearDocTemplate() {
    if (!window.confirm('Reset to app default? This clears the agency\'s custom profile.')) return;
    setDocProfileSaving(true);
    try {
      const ok = await agencyDb.saveDocTemplate(docProfile.agencyId, null);
      if (ok) {
        toast?.success?.('Reverted to app default');
        loadDocTemplate();
      } else {
        toast?.error?.('Could not reset');
      }
    } finally {
      setDocProfileSaving(false);
    }
  }

  // ── PAAL Document Upload & Extraction ────────────────────────────────
  // Workflow:
  //   1. Admin picks a PDF (handlePaalFileChange) → file uploaded to storage
  //      and structured thresholds extracted by Penny → preview form opens.
  //   2. Admin reviews/edits values in preview form (paalPreview state).
  //   3. Admin clicks Save → values written to doc_template.paal; preview closes.
  //   4. If admin clicks Cancel, the preview discards (file remains in storage,
  //      but no thresholds saved). They can re-trigger by uploading again.

  // File → base64 helper (no DataURI prefix)
  function fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result).split(',')[1]);
      r.onerror = () => reject(new Error('Could not read file'));
      r.readAsDataURL(file);
    });
  }

  async function handlePaalFileChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf') { toast?.error?.('PAAL must be a PDF file'); return; }
    if (!docProfile?.agencyId) { toast?.error?.('No agency context'); return; }
    if (!callAI) { toast?.error?.('AI is unavailable — try again later'); return; }
    setPaalExtracting(true);
    try {
      // 1. Upload PDF to storage
      const base64 = await fileToBase64(file);
      const uploadOk = await agencyDb.uploadPaalFile(docProfile.agencyId, base64, file.name);
      if (!uploadOk) { toast?.error?.('Upload failed'); return; }
      // 2. Ask Penny to extract structured thresholds
      const extracted = await extractPaalFromPdf(base64);
      if (!extracted) { toast?.error?.('Could not extract values — fill in manually'); }
      // 3. Open preview form (seeded with extracted values, or sensible defaults)
      setPaalPreview({
        tier: extracted?.tier || '',
        external_approver: extracted?.external_approver || 'Department of General Services (DGS)',
        it_self_approve_max: extracted?.it_self_approve_max ?? 200000,
        non_it_goods_self_approve_max: extracted?.non_it_goods_self_approve_max ?? 100000,
        non_it_services_self_approve_max: extracted?.non_it_services_self_approve_max ?? 0,
        notes: extracted?.notes || '',
      });
      // Update meta (filename now reflects the new upload)
      setPaalMeta({ fileName: file.name, uploadedAt: new Date().toISOString() });
    } catch (err) {
      console.error('PAAL upload/extract error:', err);
      toast?.error?.('Failed: ' + err.message);
    } finally {
      setPaalExtracting(false);
      // Reset the file input so the same file can be re-picked if needed
      e.target.value = '';
    }
  }

  // Call Penny with the uploaded PAAL PDF and ask for structured thresholds.
  // Returns an object matching the v1 schema or null on failure.
  async function extractPaalFromPdf(base64) {
    const sys = `You are extracting structured values from a Purchasing Authority Approval (PAAL) document issued by the California Department of General Services (DGS).

The PAAL is a matrix of dollar thresholds keyed by procurement method × category (IT / Non-IT Goods / Non-IT Services). Each threshold is the maximum dollar amount the agency can self-approve before requiring external (typically DGS) approval.

Extract the following values from the document and return ONLY a valid JSON object (no preamble, no code fences, no commentary):

{
  "tier": "Tier 1" | "Tier 2" | "Tier 3" | "Tier 4" | "" (whichever the document shows),
  "external_approver": "Department of General Services (DGS)" (or whatever name the document uses for the approval authority above the threshold),
  "it_self_approve_max": <number — the IT threshold for OPEN MARKET COMPETITIVE acquisition (typically the first row of the IT column)>,
  "non_it_goods_self_approve_max": <number — the Non-IT Goods threshold for OPEN MARKET COMPETITIVE acquisition>,
  "non_it_services_self_approve_max": <number — the Non-IT Services threshold for OPEN MARKET COMPETITIVE acquisition; use 0 if the document shows N/A for Non-IT Services Open Market (meaning no self-approval authority)>,
  "notes": "<plain-text summary of any unusual exemptions, asterisked rules, SB/DVBE thresholds, or special category rules that affect routine procurement. Aim for 1-3 short sentences. Skip if nothing notable.>"
}

Rules:
- Return only the JSON object. No surrounding text.
- If a value is N/A in the document, use 0.
- All dollar amounts are numbers, not strings (e.g., 200000 not "$200,000").
- The "OPEN MARKET COMPETITIVE" row is what determines the baseline self-approval threshold for most contracts; use that for the three _max fields.`;

    try {
      const raw = await callAI(apiKey, {
        system: sys,
        model: 'claude-sonnet-4-6',
        max_tokens: 1200,
        messages: [{
          role: 'user',
          content: [
            { type: 'document', source: { type: 'base64', media_type: 'application/pdf', data: base64 } },
            { type: 'text', text: 'Extract the structured PAAL values per the instructions and return JSON only.' },
          ],
        }],
        tag: 'paal_extract',
      });
      const cleaned = String(raw || '').trim().replace(/^```(?:json)?\s*/i, '').replace(/```\s*$/i, '').trim();
      const obj = JSON.parse(cleaned);
      // Coerce numeric fields just in case
      const num = (v) => {
        if (typeof v === 'number') return v;
        if (typeof v === 'string') {
          const n = parseFloat(v.replace(/[^0-9.\-]/g, ''));
          return Number.isFinite(n) ? n : 0;
        }
        return 0;
      };
      return {
        tier: String(obj.tier || ''),
        external_approver: String(obj.external_approver || 'Department of General Services (DGS)'),
        it_self_approve_max: num(obj.it_self_approve_max),
        non_it_goods_self_approve_max: num(obj.non_it_goods_self_approve_max),
        non_it_services_self_approve_max: num(obj.non_it_services_self_approve_max),
        notes: String(obj.notes || ''),
      };
    } catch (e) {
      console.error('extractPaalFromPdf failed:', e);
      return null;
    }
  }

  async function savePaalPreview() {
    if (!paalPreview || !docProfile?.agencyId) return;
    setPaalSaving(true);
    try {
      // Coerce numeric fields before save
      const toSave = {
        tier: String(paalPreview.tier || ''),
        external_approver: String(paalPreview.external_approver || 'Department of General Services (DGS)'),
        it_self_approve_max: Number(paalPreview.it_self_approve_max) || 0,
        non_it_goods_self_approve_max: Number(paalPreview.non_it_goods_self_approve_max) || 0,
        non_it_services_self_approve_max: Number(paalPreview.non_it_services_self_approve_max) || 0,
        notes: String(paalPreview.notes || ''),
        extracted_at: new Date().toISOString(),
      };
      const ok = await agencyDb.savePaalThresholds(docProfile.agencyId, toSave);
      if (ok) {
        toast?.success?.('PAAL thresholds saved');
        setPaalValues(toSave);
        setPaalPreview(null);
        // Reload doc_template so advanced JSON view is current
        loadDocTemplate();
      } else {
        toast?.error?.('Save failed — you may not have permission');
      }
    } finally {
      setPaalSaving(false);
    }
  }

  async function downloadPaalFile() {
    if (!docProfile?.agencyId) return;
    const base64 = await agencyDb.getPaalFile(docProfile.agencyId);
    if (!base64) { toast?.error?.('No PAAL file on record'); return; }
    const link = document.createElement('a');
    link.href = 'data:application/pdf;base64,' + base64;
    link.download = paalMeta?.fileName || 'paal.pdf';
    document.body.appendChild(link); link.click(); document.body.removeChild(link);
  }

  // ── Term-Clause Review ───────────────────────────────────────────────
  // Penny compares the current Term sub-letters against the Term clauses
  // in the agency's Exhibit A templates and returns structured findings:
  //   - missing_subletter: a sub-letter present in templates but not in current
  //   - wording_difference: a current sub-letter where template wording differs
  // Admin reviews findings, checks ones to apply, and saves. Applied findings
  // mutate doc_template.term_clause.subletters.

  // Open the review modal and kick off extraction
  async function startTermReview() {
    if (!docProfile?.agencyId) { toast?.error?.('No agency context'); return; }
    if (!callAI) { toast?.error?.('AI is unavailable — try again later'); return; }
    if (!uploadToAnthropicFiles) { toast?.error?.('File upload helper missing'); return; }

    // Find Exhibit A templates the agency can use. Templates store their
    // applicable exhibits as an `exhibits` array (e.g. ["A","B","D"]). Match
    // any template that includes "A" — regardless of contract type — since
    // this is an agency-level review, not a per-contract draft.
    const exhibitATemplates = (templates || []).filter(t =>
      t && Array.isArray(t.exhibits) && t.exhibits.includes('A')
    ).slice(0, 4);

    if (exhibitATemplates.length === 0) {
      toast?.warn?.('No Exhibit A templates in the library — upload templates first');
      return;
    }

    setTermReviewOpen(true);
    setTermReviewLoading(true);
    setTermFindings([]);
    setTermFindingsChecked({});
    try {
      const findings = await runTermReview(exhibitATemplates);
      setTermFindings(findings || []);
      const checked = {};
      (findings || []).forEach(f => { checked[f.id] = f.severity === 'recommended'; });
      setTermFindingsChecked(checked);
    } catch (err) {
      console.error('Term review error:', err);
      toast?.error?.('Review failed: ' + err.message);
    } finally {
      setTermReviewLoading(false);
    }
  }

  // Build the prompt + call Penny. For each template, fetch the PDF bytes
  // from Supabase storage and upload to Anthropic Files API (same pattern
  // DraftTab uses for SOW review). Skip any template whose stored bytes
  // aren't a valid PDF (docx-in-storage from older uploads).
  async function runTermReview(exhibitATemplates) {
    const docs = [];
    const skipped = [];
    for (const t of exhibitATemplates) {
      try {
        const b64 = await templatesDb.getPdf(t.id);
        if (!b64) {
          skipped.push((t.name || 'Untitled') + ' (file missing)');
          continue;
        }
        // PDFs start with "%PDF" → "JVBERi" in base64
        if (!b64.startsWith('JVBERi')) {
          skipped.push((t.name || 'Untitled') + ' (not a PDF)');
          continue;
        }
        const fileId = await uploadToAnthropicFiles(apiKey, b64, t.fileName || (t.name + '.pdf'));
        docs.push({ type: 'document', source: { type: 'file', file_id: fileId } });
      } catch (e) {
        skipped.push((t.name || 'Untitled') + ' (' + (e.message || 'upload failed') + ')');
      }
    }
    if (skipped.length > 0) {
      toast?.warn?.(skipped.length + ' template' + (skipped.length > 1 ? 's' : '') + ' skipped: ' + skipped.join(', '));
    }
    if (docs.length === 0) {
      throw new Error('No usable Exhibit A templates — none of the matched templates had valid PDF bytes in storage.');
    }

    const templateSummary = exhibitATemplates.map(t =>
      `- ${t.name || 'Untitled'} (${t.type || 'general'})`
    ).join('\n');

    const currentText = (termSubletters || []).map((s, i) =>
      `${String.fromCharCode(65 + i)}. ${s.text}`
    ).join('\n');

    const sys = `You are Penny, the GoldenScope contracting assistant. You are reviewing the Term-of-Performance clause that the agency uses in every Exhibit A. Compare the CURRENT clause against the Term clauses found in the agency's Exhibit A TEMPLATES. Return STRUCTURED FINDINGS as JSON only — no preamble, no markdown, no code fences.

The current Term clause uses these placeholder tokens which the docx builder substitutes at draft time. Preserve them when proposing new wording:
  {start_date}, {end_date}, {approver}, {agency_short_name}, {contract_title}, {contractor_name}

For each TEMPLATE, find the "TERM OF PERFORMANCE" / "TERM" section and look at its sub-letters. Compare to the current sub-letters. Identify:

1. SUB-LETTERS PRESENT IN TEMPLATES BUT MISSING FROM CURRENT — e.g., an "option to extend" clause, a "termination for convenience" reference, a clause about contract suspension. Only flag if the same TYPE of clause appears in MORE THAN ONE template (i.e., consensus).

2. WORDING DIFFERENCES on current sub-letters — e.g., templates consistently use "mutual written consent" where current says "consent of both parties". Only flag if the difference is consistent across templates AND substantive (ignore trivial differences like comma placement).

Return JSON in this exact shape:
{
  "findings": [
    {
      "id": "f1",
      "type": "missing_subletter",
      "severity": "recommended" | "suggestion",
      "title": "<short title — 5-8 words>",
      "rationale": "<one or two sentences explaining why this matters>",
      "proposed_subletter": {
        "id": "<stable kebab-case id derived from the topic, e.g. 'option-to-extend'>",
        "text": "<the proposed sub-letter text, using placeholder tokens where applicable>"
      }
    },
    {
      "id": "f2",
      "type": "wording_difference",
      "severity": "recommended" | "suggestion",
      "title": "<short title>",
      "rationale": "<short explanation>",
      "target_subletter_id": "<id of the existing sub-letter to modify (e.g. 'commencement', 'amendment')>",
      "current_text": "<the current text verbatim>",
      "proposed_text": "<the proposed replacement text>"
    }
  ]
}

Use severity "recommended" when 2+ templates agree on the change. Use "suggestion" when only one template differs. If you find no significant differences, return {"findings": []}.

Be conservative — only flag CONSEQUENTIAL changes. Do NOT flag every minor word choice.`;

    const userText = `CURRENT TERM CLAUSE (in the agency's app default):\n\n${currentText}\n\n` +
      `Templates attached for comparison:\n${templateSummary}\n\n` +
      `Find the "TERM OF PERFORMANCE" section in each attached template and produce findings per the instructions above. Return JSON only.`;

    const raw = await callAI(apiKey, {
      system: sys,
      model: 'claude-sonnet-4-6',
      max_tokens: 2500,
      messages: [{
        role: 'user',
        content: [...docs, { type: 'text', text: userText }],
      }],
      tag: 'term_review',
    });

    const cleaned = String(raw || '').trim().replace(/^```(?:json)?\s*/i, '').replace(/```\s*$/i, '').trim();
    let parsed;
    try {
      parsed = JSON.parse(cleaned);
    } catch (e) {
      console.error('Term review JSON parse failed:', e, 'raw:', cleaned);
      return [];
    }
    return Array.isArray(parsed?.findings) ? parsed.findings : [];
  }

  // Apply the user-approved findings to the current sub-letters and save.
  async function applyTermFindings() {
    if (!docProfile?.agencyId) return;
    const accepted = (termFindings || []).filter(f => termFindingsChecked[f.id]);
    if (accepted.length === 0) {
      toast?.info?.('No findings selected — nothing to apply');
      return;
    }
    setTermApplying(true);
    try {
      // Start from the current sub-letters (clone for safety)
      let next = (termSubletters || []).map(s => ({ ...s }));

      for (const f of accepted) {
        if (f.type === 'wording_difference' && f.target_subletter_id) {
          // Find by id, replace text
          const idx = next.findIndex(s => s.id === f.target_subletter_id);
          if (idx >= 0) {
            next[idx] = { ...next[idx], text: f.proposed_text, source: 'penny_recommendation' };
          }
        } else if (f.type === 'missing_subletter' && f.proposed_subletter) {
          // Append, but skip if id already exists (de-duplicate against re-review)
          const propId = f.proposed_subletter.id || ('letter-' + (next.length + 1));
          if (!next.some(s => s.id === propId)) {
            next.push({
              id: propId,
              text: f.proposed_subletter.text,
              source: 'penny_recommendation',
              active: true,
            });
          }
        }
      }

      const newTermClause = {
        subletters: next,
        last_reviewed_at: new Date().toISOString(),
        last_reviewed_against: (templates || []).slice(0, 4).map(t => t.id),
      };
      const ok = await agencyDb.saveTermClause(docProfile.agencyId, newTermClause);
      if (ok) {
        toast?.success?.(accepted.length + ' change' + (accepted.length === 1 ? '' : 's') + ' applied');
        setTermClause(newTermClause);
        setTermSubletters(next);
        setTermReviewOpen(false);
        setTermFindings([]);
        setTermFindingsChecked({});
        // Reload to refresh advanced JSON view
        loadDocTemplate();
      } else {
        toast?.error?.('Save failed');
      }
    } catch (err) {
      console.error('applyTermFindings error:', err);
      toast?.error?.('Failed: ' + err.message);
    } finally {
      setTermApplying(false);
    }
  }

  // Toggle a sub-letter on/off without going through review (for fine-tuning).
  async function toggleTermSubletter(idx) {
    if (!docProfile?.agencyId) return;
    const next = (termSubletters || []).map((s, i) =>
      i === idx ? { ...s, active: s.active === false } : s
    );
    setTermSubletters(next);
    const newTermClause = {
      ...(termClause || {}),
      subletters: next,
    };
    const ok = await agencyDb.saveTermClause(docProfile.agencyId, newTermClause);
    if (ok) {
      setTermClause(newTermClause);
      toast?.success?.('Updated');
    } else {
      toast?.error?.('Save failed');
    }
  }

  // Save an edited sub-letter's text (from inline editing in the list).
  async function saveEditedSubletter(idx, newText) {
    if (!docProfile?.agencyId) return;
    const next = (termSubletters || []).map((s, i) =>
      i === idx ? { ...s, text: newText, source: 'manual' } : s
    );
    setTermSubletters(next);
    setTermEditing(null);
    const newTermClause = { ...(termClause || {}), subletters: next };
    const ok = await agencyDb.saveTermClause(docProfile.agencyId, newTermClause);
    if (ok) {
      setTermClause(newTermClause);
      toast?.success?.('Saved');
    } else {
      toast?.error?.('Save failed');
    }
  }

  // Reset to built-in defaults
  async function resetTermClause() {
    if (!window.confirm('Reset Term clause to defaults? This removes any Penny-recommended or manually-added sub-letters.')) return;
    if (!docProfile?.agencyId) return;
    const newTermClause = {
      subletters: DEFAULT_TERM_SUBLETTERS.map(s => ({ ...s })),
      last_reviewed_at: null,
      last_reviewed_against: [],
    };
    const ok = await agencyDb.saveTermClause(docProfile.agencyId, newTermClause);
    if (ok) {
      setTermClause(newTermClause);
      setTermSubletters(newTermClause.subletters);
      toast?.success?.('Reset to defaults');
    } else {
      toast?.error?.('Reset failed');
    }
  }

  // ── Exhibit B Template Extraction ────────────────────────────────────
  // Penny reads the agency's Exhibit B templates (Budget Detail and Payment
  // Provisions) and extracts a canonical set of sections — payment schedule,
  // invoicing, rates, withholding, etc. The result becomes the agency baseline
  // for future Exhibit B drafts; per-contract drafts customize this baseline
  // rather than synthesizing from scratch.
  //
  // The current sections (if any) are NOT compared the way Term Review compares —
  // Exhibit B is a whole document, not a small clause, so we don't do
  // wording_difference findings. Instead, Penny re-extracts a fresh set
  // each time, and the admin either accepts the new set wholesale or hand-edits
  // before saving.

  async function startExhibitBReview() {
    if (!docProfile?.agencyId) { toast?.error?.('No agency context'); return; }
    if (!callAI) { toast?.error?.('AI is unavailable — try again later'); return; }
    if (!uploadToAnthropicFiles) { toast?.error?.('File upload helper missing'); return; }

    const exhibitBTemplates = (templates || []).filter(t =>
      t && Array.isArray(t.exhibits) && t.exhibits.includes('B')
    ).slice(0, 4);

    if (exhibitBTemplates.length === 0) {
      toast?.warn?.('No Exhibit B templates in the library — upload templates first');
      return;
    }

    setExbReviewOpen(true);
    setExbReviewLoading(true);
    setExbExtracted(null);
    try {
      const sections = await runExhibitBExtraction(exhibitBTemplates);
      setExbExtracted(sections);
    } catch (err) {
      console.error('Exhibit B extraction error:', err);
      toast?.error?.('Extraction failed: ' + err.message);
      setExbReviewOpen(false);
    } finally {
      setExbReviewLoading(false);
    }
  }

  // Run Penny against the Exhibit B templates and return an extracted array
  // of sections (each with id, heading, text). Same upload pattern as the
  // Term review — fetch each template's PDF bytes from storage, upload via
  // Files API, attach as document blocks.
  async function runExhibitBExtraction(exhibitBTemplates) {
    const docs = [];
    const skipped = [];
    for (const t of exhibitBTemplates) {
      try {
        const b64 = await templatesDb.getPdf(t.id);
        if (!b64) { skipped.push((t.name || 'Untitled') + ' (file missing)'); continue; }
        if (!b64.startsWith('JVBERi')) { skipped.push((t.name || 'Untitled') + ' (not a PDF)'); continue; }
        const fileId = await uploadToAnthropicFiles(apiKey, b64, t.fileName || (t.name + '.pdf'));
        docs.push({ type: 'document', source: { type: 'file', file_id: fileId } });
      } catch (e) {
        skipped.push((t.name || 'Untitled') + ' (' + (e.message || 'upload failed') + ')');
      }
    }
    if (skipped.length > 0) {
      toast?.warn?.(skipped.length + ' template' + (skipped.length > 1 ? 's' : '') + ' skipped: ' + skipped.join(', '));
    }
    if (docs.length === 0) {
      throw new Error('No usable Exhibit B templates — none of the matched templates had valid PDF bytes.');
    }

    const sys = `You are Penny, the GoldenScope contracting assistant. Your task: read the agency's Exhibit B templates (Budget Detail and Payment Provisions) and extract a canonical, reusable set of sections that will serve as the agency's BASELINE Exhibit B template. Future contract drafts will start from this baseline and only customize per-contract values.

Return STRUCTURED JSON only — no preamble, no markdown, no code fences.

For each section found in the templates, return:
  - id: stable kebab-case identifier (e.g., "payment-schedule", "invoicing", "rates-and-payment-terms")
  - heading: the section heading as it would appear in the docx (e.g., "1. PAYMENT SCHEDULE" or "PAYMENT TERMS")
  - text: the full clause text. Use these PLACEHOLDER TOKENS where contract-specific values would go:
      {contract_total}      — total dollar amount (e.g., $250,000)
      {contractor_name}     — contractor's legal name
      {agreement_num}       — agreement number
      {contract_title}      — short title of the contract
      {agency_short_name}   — agency name in body text (e.g., "Energy Safety")
      {net_terms_days}      — net payment terms in days (e.g., 30, 45)
      {invoicing_address}   — where invoices go
      {end_date}            — contract end date
      {payment_schedule}    — placeholder for the per-contract payment schedule (admin/Penny fills per-contract)

Rules:
1. Synthesize across templates — if multiple templates have the same section with slightly different wording, choose the wording that appears in the majority OR is the most general/reusable.
2. Order sections as they typically appear in Exhibit B (payment schedule first, then invoicing, rates, withholding, dispute resolution, etc.).
3. Aim for 5-10 sections total. Skip section structures that appear in only one template unless they're clearly standard for the contract category.
4. Each section's text should be FULL CLAUSE LANGUAGE, not a summary. Preserve formal California state contract voice.
5. Where dollar amounts, names, dates, or specific addresses appear in the source, REPLACE with the appropriate placeholder token.

Return shape:
{
  "sections": [
    {
      "id": "payment-schedule",
      "heading": "1. PAYMENT SCHEDULE",
      "text": "The State shall pay the Contractor in arrears, monthly, upon submission and acceptance of properly completed invoices..."
    },
    ...
  ]
}`;

    const userText = 'Extract a canonical set of Exhibit B sections from these templates per the instructions above. Return JSON only.';

    const raw = await callAI(apiKey, {
      system: sys,
      model: 'claude-sonnet-4-6',
      max_tokens: 4000,
      messages: [{
        role: 'user',
        content: [...docs, { type: 'text', text: userText }],
      }],
      tag: 'exhibit_b_template_extract',
    });

    const cleaned = String(raw || '').trim().replace(/^```(?:json)?\s*/i, '').replace(/```\s*$/i, '').trim();
    let parsed;
    try {
      parsed = JSON.parse(cleaned);
    } catch (e) {
      console.error('Exhibit B extraction JSON parse failed:', e, 'raw:', cleaned);
      throw new Error('Penny returned an unparseable response. Try again.');
    }
    if (!Array.isArray(parsed?.sections) || parsed.sections.length === 0) {
      throw new Error('No sections returned. Check that templates contain a recognizable Exhibit B.');
    }
    return parsed.sections.map((s, i) => ({
      id: String(s.id || ('section-' + (i + 1))),
      heading: String(s.heading || ''),
      text: String(s.text || ''),
      source: 'penny_recommendation',
      active: true,
    }));
  }

  // Save the extracted (and possibly hand-edited) Exhibit B template baseline.
  async function saveExhibitBTemplate() {
    if (!docProfile?.agencyId || !exbExtracted) return;
    setExbSaving(true);
    try {
      const newTemplate = {
        sections: exbExtracted,
        last_reviewed_at: new Date().toISOString(),
        last_reviewed_against: (templates || []).filter(t => t.exhibits?.includes('B')).slice(0, 4).map(t => t.id),
      };
      const ok = await agencyDb.saveExhibitBTemplate(docProfile.agencyId, newTemplate);
      if (ok) {
        setExhibitBTemplate(newTemplate);
        setExbSections(newTemplate.sections);
        setExbReviewOpen(false);
        setExbExtracted(null);
        toast?.success?.('Exhibit B template saved');
        loadDocTemplate();
      } else {
        toast?.error?.('Save failed');
      }
    } catch (err) {
      console.error('saveExhibitBTemplate error:', err);
      toast?.error?.('Failed: ' + err.message);
    } finally {
      setExbSaving(false);
    }
  }

  // Edit a section inline (heading or text) in the preview before save.
  function updateExtractedSection(idx, field, value) {
    setExbExtracted(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  }

  // Remove a section from the extracted preview (admin doesn't want it).
  function removeExtractedSection(idx) {
    setExbExtracted(prev => prev.filter((_, i) => i !== idx));
  }

  // Toggle a saved section's active flag without going through review.
  async function toggleExbSection(idx) {
    if (!docProfile?.agencyId) return;
    const next = (exbSections || []).map((s, i) =>
      i === idx ? { ...s, active: s.active === false } : s
    );
    setExbSections(next);
    const newTemplate = { ...(exhibitBTemplate || {}), sections: next };
    const ok = await agencyDb.saveExhibitBTemplate(docProfile.agencyId, newTemplate);
    if (ok) {
      setExhibitBTemplate(newTemplate);
      toast?.success?.('Updated');
    } else {
      toast?.error?.('Save failed');
    }
  }

  // Save an inline-edited section's text (from the saved list, not preview).
  async function saveEditedExbSection(idx, newHeading, newText) {
    if (!docProfile?.agencyId) return;
    const next = (exbSections || []).map((s, i) =>
      i === idx ? { ...s, heading: newHeading, text: newText, source: 'manual' } : s
    );
    setExbSections(next);
    setExbEditing(null);
    const newTemplate = { ...(exhibitBTemplate || {}), sections: next };
    const ok = await agencyDb.saveExhibitBTemplate(docProfile.agencyId, newTemplate);
    if (ok) {
      setExhibitBTemplate(newTemplate);
      toast?.success?.('Saved');
    } else {
      toast?.error?.('Save failed');
    }
  }

  // Reset — clear the agency-level Exhibit B template entirely.
  async function resetExhibitBTemplate() {
    if (!window.confirm('Clear the Exhibit B template? This removes the agency baseline. Penny will fall back to synthesizing from templates on each draft.')) return;
    if (!docProfile?.agencyId) return;
    const ok = await agencyDb.saveExhibitBTemplate(docProfile.agencyId, null);
    if (ok) {
      setExhibitBTemplate(null);
      setExbSections([]);
      toast?.success?.('Cleared');
    } else {
      toast?.error?.('Reset failed');
    }
  }

  // ── Exhibit D Template Extraction ────────────────────────────────────
  // Same pattern as Exhibit B but for Special Terms and Conditions.
  // Penny extracts canonical sections from the agency's Exhibit D templates;
  // admin reviews/edits/saves; per-contract drafts customize the baseline
  // instead of synthesizing each Exhibit D from scratch.

  async function startExhibitDReview() {
    if (!docProfile?.agencyId) { toast?.error?.('No agency context'); return; }
    if (!callAI) { toast?.error?.('AI is unavailable — try again later'); return; }
    if (!uploadToAnthropicFiles) { toast?.error?.('File upload helper missing'); return; }

    const exhibitDTemplates = (templates || []).filter(t =>
      t && Array.isArray(t.exhibits) && t.exhibits.includes('D')
    ).slice(0, 4);

    if (exhibitDTemplates.length === 0) {
      toast?.warn?.('No Exhibit D templates in the library — upload templates first');
      return;
    }

    setExdReviewOpen(true);
    setExdReviewLoading(true);
    setExdExtracted(null);
    try {
      const sections = await runExhibitDExtraction(exhibitDTemplates);
      setExdExtracted(sections);
    } catch (err) {
      console.error('Exhibit D extraction error:', err);
      toast?.error?.('Extraction failed: ' + err.message);
      setExdReviewOpen(false);
    } finally {
      setExdReviewLoading(false);
    }
  }

  async function runExhibitDExtraction(exhibitDTemplates) {
    const docs = [];
    const skipped = [];
    for (const t of exhibitDTemplates) {
      try {
        const b64 = await templatesDb.getPdf(t.id);
        if (!b64) { skipped.push((t.name || 'Untitled') + ' (file missing)'); continue; }
        if (!b64.startsWith('JVBERi')) { skipped.push((t.name || 'Untitled') + ' (not a PDF)'); continue; }
        const fileId = await uploadToAnthropicFiles(apiKey, b64, t.fileName || (t.name + '.pdf'));
        docs.push({ type: 'document', source: { type: 'file', file_id: fileId } });
      } catch (e) {
        skipped.push((t.name || 'Untitled') + ' (' + (e.message || 'upload failed') + ')');
      }
    }
    if (skipped.length > 0) {
      toast?.warn?.(skipped.length + ' template' + (skipped.length > 1 ? 's' : '') + ' skipped: ' + skipped.join(', '));
    }
    if (docs.length === 0) {
      throw new Error('No usable Exhibit D templates — none of the matched templates had valid PDF bytes.');
    }

    const sys = `You are Penny, the GoldenScope contracting assistant. Your task: read the agency's Exhibit D templates (Special Terms and Conditions) and extract a canonical, reusable set of sections that will serve as the agency's BASELINE Exhibit D. Future contract drafts will start from this baseline.

Return STRUCTURED JSON only — no preamble, no markdown, no code fences.

For each section found in the templates, return:
  - id: stable kebab-case identifier (e.g., "termination-for-convenience", "confidentiality", "intellectual-property", "force-majeure")
  - heading: the section heading as it would appear in the docx (e.g., "1. TERMINATION FOR CONVENIENCE" or "CONFIDENTIALITY")
  - text: the full clause text. Use these PLACEHOLDER TOKENS where contract-specific values would go:
      {contractor_name}    — contractor's legal name
      {agreement_num}      — agreement number
      {contract_title}     — short title of the contract
      {agency_short_name}  — agency name in body text
      {end_date}           — contract end date

Special Terms typically cover topics like:
  • Termination (for convenience, for cause)
  • Indemnification and liability
  • Confidentiality / non-disclosure
  • Intellectual property and work product ownership
  • Force majeure
  • Conflict of interest
  • Audit rights and records retention
  • Insurance requirements
  • Notices
  • Subcontracting
  • Compliance with applicable laws
  • Governing law and dispute resolution

Rules:
1. Synthesize across templates — choose the wording from the majority OR the most general/reusable variant.
2. Order sections in a typical Exhibit D sequence (termination → indemnity → confidentiality → IP → other).
3. Aim for 8-15 sections. Skip section structures that appear in only one template unless they're clearly standard.
4. Each section's text should be FULL CLAUSE LANGUAGE, not a summary. Preserve formal California state contract voice.
5. Replace contractor-specific names, dollar amounts, dates, and addresses with placeholder tokens.

Return shape:
{
  "sections": [
    {
      "id": "termination-for-convenience",
      "heading": "1. TERMINATION FOR CONVENIENCE",
      "text": "{agency_short_name} may terminate this Contract..."
    },
    ...
  ]
}`;

    const userText = 'Extract a canonical set of Exhibit D sections from these templates per the instructions above. Return JSON only.';

    const raw = await callAI(apiKey, {
      system: sys,
      model: 'claude-sonnet-4-6',
      max_tokens: 6000,
      messages: [{
        role: 'user',
        content: [...docs, { type: 'text', text: userText }],
      }],
      tag: 'exhibit_d_template_extract',
    });

    const cleaned = String(raw || '').trim().replace(/^```(?:json)?\s*/i, '').replace(/```\s*$/i, '').trim();
    let parsed;
    try {
      parsed = JSON.parse(cleaned);
    } catch (e) {
      console.error('Exhibit D extraction JSON parse failed:', e, 'raw:', cleaned);
      throw new Error('Penny returned an unparseable response. Try again.');
    }
    if (!Array.isArray(parsed?.sections) || parsed.sections.length === 0) {
      throw new Error('No sections returned. Check that templates contain a recognizable Exhibit D.');
    }
    return parsed.sections.map((s, i) => ({
      id: String(s.id || ('section-' + (i + 1))),
      heading: String(s.heading || ''),
      text: String(s.text || ''),
      source: 'penny_recommendation',
      active: true,
    }));
  }

  async function saveExhibitDTemplate() {
    if (!docProfile?.agencyId || !exdExtracted) return;
    setExdSaving(true);
    try {
      const newTemplate = {
        sections: exdExtracted,
        last_reviewed_at: new Date().toISOString(),
        last_reviewed_against: (templates || []).filter(t => t.exhibits?.includes('D')).slice(0, 4).map(t => t.id),
      };
      const ok = await agencyDb.saveExhibitDTemplate(docProfile.agencyId, newTemplate);
      if (ok) {
        setExhibitDTemplate(newTemplate);
        setExdSections(newTemplate.sections);
        setExdReviewOpen(false);
        setExdExtracted(null);
        toast?.success?.('Exhibit D template saved');
        loadDocTemplate();
      } else {
        toast?.error?.('Save failed');
      }
    } catch (err) {
      console.error('saveExhibitDTemplate error:', err);
      toast?.error?.('Failed: ' + err.message);
    } finally {
      setExdSaving(false);
    }
  }

  function updateExtractedDSection(idx, field, value) {
    setExdExtracted(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  }
  function removeExtractedDSection(idx) {
    setExdExtracted(prev => prev.filter((_, i) => i !== idx));
  }

  // ── Clause Library helpers ────────────────────────────────────────────
  async function saveClauseLibraryNow(updated) {
    if (!docProfile?.agencyId) return;
    setClauseSaving(true);
    try {
      const ok = await agencyDb.saveClauseLibrary(docProfile.agencyId, updated);
      if (ok) {
        setClauseLibrary(updated);
        toast?.success?.('Clause library saved');
      } else {
        toast?.error?.('Save failed');
      }
    } catch (e) {
      toast?.error?.('Failed: ' + e.message);
    } finally {
      setClauseSaving(false);
    }
  }

  function addClause(exhibit) {
    const newClause = {
      id: crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(),
      sectionName: clauseEditForm.sectionName,
      language: clauseEditForm.language,
      locked: clauseEditForm.locked,
      required: clauseEditForm.required,
      sortOrder: (clauseLibrary[exhibit]?.length || 0) + 1,
      createdAt: new Date().toISOString(),
    };
    const updated = { ...clauseLibrary, [exhibit]: [...(clauseLibrary[exhibit] || []), newClause] };
    saveClauseLibraryNow(updated);
    setClauseAdding(null);
    setClauseEditForm({ sectionName: '', language: '', locked: true, required: false });
  }

  function updateClause(exhibit, idx, updates) {
    const updated = {
      ...clauseLibrary,
      [exhibit]: clauseLibrary[exhibit].map((c, i) => i === idx ? { ...c, ...updates, updatedAt: new Date().toISOString() } : c),
    };
    saveClauseLibraryNow(updated);
    setClauseEditing(null);
  }

  function deleteClause(exhibit, idx) {
    const updated = { ...clauseLibrary, [exhibit]: clauseLibrary[exhibit].filter((_, i) => i !== idx) };
    saveClauseLibraryNow(updated);
  }

  function toggleClauseLock(exhibit, idx) {
    const c = clauseLibrary[exhibit][idx];
    updateClause(exhibit, idx, { locked: !c.locked });
  }

  function toggleClauseRequired(exhibit, idx) {
    const c = clauseLibrary[exhibit][idx];
    updateClause(exhibit, idx, { required: !c.required });
  }

  async function toggleExdSection(idx) {
    if (!docProfile?.agencyId) return;
    const next = (exdSections || []).map((s, i) =>
      i === idx ? { ...s, active: s.active === false } : s
    );
    setExdSections(next);
    const newTemplate = { ...(exhibitDTemplate || {}), sections: next };
    const ok = await agencyDb.saveExhibitDTemplate(docProfile.agencyId, newTemplate);
    if (ok) {
      setExhibitDTemplate(newTemplate);
      toast?.success?.('Updated');
    } else {
      toast?.error?.('Save failed');
    }
  }

  async function saveEditedExdSection(idx, newHeading, newText) {
    if (!docProfile?.agencyId) return;
    const next = (exdSections || []).map((s, i) =>
      i === idx ? { ...s, heading: newHeading, text: newText, source: 'manual' } : s
    );
    setExdSections(next);
    setExdEditing(null);
    const newTemplate = { ...(exhibitDTemplate || {}), sections: next };
    const ok = await agencyDb.saveExhibitDTemplate(docProfile.agencyId, newTemplate);
    if (ok) {
      setExhibitDTemplate(newTemplate);
      toast?.success?.('Saved');
    } else {
      toast?.error?.('Save failed');
    }
  }

  async function resetExhibitDTemplate() {
    if (!window.confirm('Clear the Exhibit D template? This removes the agency baseline.')) return;
    if (!docProfile?.agencyId) return;
    const ok = await agencyDb.saveExhibitDTemplate(docProfile.agencyId, null);
    if (ok) {
      setExhibitDTemplate(null);
      setExdSections([]);
      toast?.success?.('Cleared');
    } else {
      toast?.error?.('Reset failed');
    }
  }


  async function loadPendingCount() {
    const { count } = await supabase.from('user_profiles')
      .select('id', { count: 'exact', head: true })
      .eq('agency_name', profile.agencyName || '')
      .eq('status', 'pending')
      .neq('id', currentUserId);
    setPendingCount(count || 0);
  }

  async function loadPending() {
    setLoading(true);
    const { data } = await supabase.from('user_profiles')
      .select('id, contact_name, contact_email, role, supervisor_id, team_name, created_at, status')
      .eq('agency_name', profile.agencyName || '')
      .eq('status', 'pending')
      .neq('id', currentUserId)
      .order('created_at', { ascending: false });
    setUsers(data || []);
    setLoading(false);
  }

  async function loadUsers() {
    setLoading(true);
    let query = supabase.from('user_profiles')
      .select('id, contact_name, contact_email, role, supervisor_id, team_name, status, created_at')
      .eq('agency_name', profile.agencyName || '')
      .eq('status', 'active')
      .neq('id', currentUserId)
      .order('role', { ascending: false });

    // Supervisors only see their team by default
    if (tab === 'myteam' && !isAgencySuperAdmin) {
      query = query.eq('supervisor_id', currentUserId);
    }

    const { data } = await query;
    setUsers(data || []);

    // Load all analysts for reassignment dropdown
    const { data: allAnalysts } = await supabase.from('user_profiles')
      .select('id, contact_name, team_name, supervisor_id')
      .eq('agency_name', profile.agencyName || '')
      .eq('status', 'active')
      .eq('role', 'member');
    setAnalysts(allAnalysts || []);
    setLoading(false);
  }

  async function approveUser(userId, name, selectedSupervisorId) {
    setWorking(w => ({ ...w, [userId]: true }));
    try {
      await supabase.rpc('update_user_status', { p_user_id: userId, p_status: 'active' });
      const updates = { status: 'active' };
      if (selectedSupervisorId) updates.supervisor_id = selectedSupervisorId;
      await supabase.from('user_profiles').update(updates).eq('id', userId);
      toast.success((name || 'User') + ' approved — they can now log in.');
      setPendingCount(c => Math.max(0, c - 1));
      loadPending();
    } catch (e) { toast.error('Failed: ' + e.message); }
    finally { setWorking(w => ({ ...w, [userId]: false })); }
  }

  async function rejectUser(userId, name) {
    setWorking(w => ({ ...w, [userId]: true }));
    try {
      await supabase.rpc('update_user_status', { p_user_id: userId, p_status: 'rejected' });
      await supabase.from('user_profiles').update({ status: 'rejected' }).eq('id', userId);
      toast.info((name || 'User') + ' rejected.');
      loadPending(); loadPendingCount();
    } catch (e) { toast.error('Failed: ' + e.message); }
    finally { setWorking(w => ({ ...w, [userId]: false })); }
  }

  async function reassignContract(contractId, newOwnerId, newOwnerName) {
    try {
      const { error } = await supabase.from('contracts')
        .update({ owner_id: newOwnerId, analyst_name: newOwnerName })
        .eq('id', contractId);
      if (error) throw error;
      toast.success('Contract reassigned to ' + newOwnerName);
      setReassignTarget(null);
    } catch (e) { toast.error('Reassign failed: ' + e.message); }
  }

  async function changeUserRole(userId, name, newRole) {
    setWorking(w => ({ ...w, [userId]: true }));
    try {
      await supabase.rpc('update_user_status', { p_user_id: userId, p_status: 'active' });
      await supabase.from('user_profiles').update({ role: newRole }).eq('id', userId);
      // Update auth metadata role
      await supabase.auth.admin?.updateUserById?.(userId, { user_metadata: { role: newRole } });
      toast.success((name || 'User') + ' role updated to ' + ROLE_LABELS[newRole]);
      setUserAction(null);
      loadUsers();
    } catch (e) { toast.error('Failed: ' + e.message); }
    finally { setWorking(w => ({ ...w, [userId]: false })); }
  }

  async function changeUserTeam(userId, name, newSupervisorId) {
    setWorking(w => ({ ...w, [userId]: true }));
    try {
      await supabase.from('user_profiles').update({ supervisor_id: newSupervisorId || null }).eq('id', userId);
      toast.success((name || 'User') + ' moved to new team');
      setUserAction(null);
      loadUsers();
    } catch (e) { toast.error('Failed: ' + e.message); }
    finally { setWorking(w => ({ ...w, [userId]: false })); }
  }

  async function deactivateUser(userId, name, reassignToId) {
    setWorking(w => ({ ...w, [userId]: true }));
    try {
      // Reject the user
      await supabase.rpc('update_user_status', { p_user_id: userId, p_status: 'rejected' });
      await supabase.from('user_profiles').update({ status: 'rejected' }).eq('id', userId);
      // Reassign their contracts if a replacement was selected
      if (reassignToId) {
        const { data: replacementProfile } = await supabase
          .from('user_profiles').select('contact_name').eq('id', reassignToId).single();
        await supabase.from('contracts')
          .update({ owner_id: reassignToId, analyst_name: replacementProfile?.contact_name || '' })
          .eq('owner_id', userId);
        // Move their team members to the replacement supervisor
        await supabase.from('user_profiles')
          .update({ supervisor_id: reassignToId })
          .eq('supervisor_id', userId);
        toast.success((name || 'User') + ' deactivated — contracts and team reassigned to ' + (replacementProfile?.contact_name || 'new user'));
      } else {
        toast.success((name || 'User') + ' deactivated');
      }
      setUserAction(null);
      loadUsers();
    } catch (e) { toast.error('Failed: ' + e.message); }
    finally { setWorking(w => ({ ...w, [userId]: false })); }
  }

  async function reinstateUser(userId, name) {
    setWorking(w => ({ ...w, [userId]: true }));
    try {
      await supabase.rpc('update_user_status', { p_user_id: userId, p_status: 'active' });
      await supabase.from('user_profiles').update({ status: 'active' }).eq('id', userId);
      toast.success((name || 'User') + ' reinstated');
      setUserAction(null);
      loadUsers();
    } catch (e) { toast.error('Failed: ' + e.message); }
    finally { setWorking(w => ({ ...w, [userId]: false })); }
  }

  // Load supervisors for team change
  async function loadSupervisors() {
    const { data } = await supabase.from('user_profiles')
      .select('id, contact_name, team_name')
      .eq('agency_name', profile.agencyName || '')
      .in('role', ['admin', 'agency_super_admin'])
      .eq('status', 'active')
      .neq('id', currentUserId);
    setSupervisors(data || []);
  }

  // Contracts visible to this admin
  const visibleContracts = (allContracts || []).filter(c => {
    if (c.archived) return false;
    if (isAgencySuperAdmin) return true; // sees all
    if (tab === 'myteam') {
      // Supervisor sees contracts owned by their team members
      const teamIds = users.filter(u => u.supervisor_id === currentUserId).map(u => u.id);
      return teamIds.includes(c.ownerId) || c.ownerId === currentUserId;
    }
    return true;
  });

  const getSupervisorName = (supId) => {
    const sup = users.find(u => u.id === supId);
    return sup?.contact_name || 'Unassigned';
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 24 }}>
      <div style={{ background: 'var(--white)', borderRadius: 'var(--r-xl, 16px)', width: '100%', maxWidth: 780, maxHeight: '88vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px rgba(0,0,0,0.25)' }}>

        {/* Header */}
        <div style={{ padding: '18px 24px', borderBottom: '1px solid var(--slate-100)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--navy-900)' }}>
              {isAgencySuperAdmin ? 'Agency Management' : 'Team Management'}
            </div>
            <div style={{ fontSize: 12, color: 'var(--slate-400)', marginTop: 2 }}>
              {profile.agencyName} · {ROLE_LABELS[profile.role] || 'Admin'}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: 'var(--slate-300)' }}>×</button>
        </div>

        {/* Tabs */}
        <div style={{ padding: '10px 24px', borderBottom: '1px solid var(--slate-100)', display: 'flex', gap: 6 }}>
          {[
            { key: 'pending', label: 'Pending Approvals', badge: pendingCount },
            { key: 'myteam', label: isAgencySuperAdmin ? 'All Supervisors & Teams' : 'My Team' },
            { key: 'allteams', label: 'All Contracts', show: isAgencySuperAdmin },
            { key: 'doctemplate', label: '📝 Doc Profile', show: isAgencyAdmin },
            { key: 'clauses', label: '🔒 Clause Library', show: isAgencyAdmin },
          ].filter(t => t.show !== false).map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} style={{
              padding: '7px 14px', borderRadius: 99, fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
              background: tab === t.key ? 'var(--navy-800)' : 'var(--slate-50)',
              color: tab === t.key ? 'var(--white)' : 'var(--slate-500)',
              border: '1px solid ' + (tab === t.key ? 'var(--navy-800)' : 'var(--slate-200)'),
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              {t.label}
              {t.badge > 0 && (
                <span style={{ background: tab === t.key ? 'rgba(255,255,255,.25)' : 'var(--red-500)', color: tab === t.key ? 'white' : 'white', borderRadius: 99, padding: '1px 6px', fontSize: 10, fontWeight: 700 }}>
                  {t.badge}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--slate-400)' }}>Loading…</div>
          ) : (
            <>
              {/* ── Pending Tab ── */}
              {tab === 'pending' && (
                users.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 40, color: 'var(--slate-400)' }}>
                    <div style={{ fontSize: 28, marginBottom: 8 }}>⏳</div>
                    <div style={{ fontWeight: 600 }}>No pending approvals</div>
                    <div style={{ fontSize: 13, marginTop: 4 }}>New analyst signups will appear here.</div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {users.map(u => (
                      <PendingUserCard key={u.id} user={u} supervisors={[...users.filter(x => ['admin','agency_super_admin'].includes(x.role)), { id: currentUserId, contact_name: profile.contactName + ' (you)' }]}
                        onApprove={approveUser} onReject={rejectUser} working={working[u.id]} />
                    ))}
                  </div>
                )
              )}

              {/* ── My Team / All Supervisors Tab ── */}
              {tab === 'myteam' && (
                users.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 40, color: 'var(--slate-400)' }}>
                    <div style={{ fontSize: 28, marginBottom: 8 }}>👥</div>
                    <div style={{ fontWeight: 600 }}>No team members yet</div>
                    <div style={{ fontSize: 13, marginTop: 4 }}>Approve pending analysts to build your team.</div>
                  </div>
                ) : (
                  <div>
                    {/* Group by supervisor for agency_super_admin */}
                    {isAgencySuperAdmin ? (
                      (() => {
                        const supervisorsList = users.filter(u => ['admin','agency_super_admin'].includes(u.role));
                        const analystsList = users.filter(u => u.role === 'member');
                        return (
                          <>
                            {supervisorsList.length > 0 && (
                              <div style={{ marginBottom: 20 }}>
                                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--slate-400)', marginBottom: 10 }}>Supervisors & Admins</div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                  {supervisorsList.map(u => <UserCard key={u.id} user={u}
                                    onAction={(action) => { loadSupervisors(); setUserAction({ user: u, action }); }} />)}
                                </div>
                              </div>
                            )}
                            {analystsList.length > 0 && (
                              <div>
                                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--slate-400)', marginBottom: 10 }}>Analysts</div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                  {analystsList.map(u => <UserCard key={u.id} user={u} supervisorName={getSupervisorName(u.supervisor_id)}
                                    onAction={(action) => { loadSupervisors(); setUserAction({ user: u, action }); }} />)}
                                </div>
                              </div>
                            )}
                          </>
                        );
                      })()
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {users.map(u => <UserCard key={u.id} user={u}
                          onAction={(action) => { loadSupervisors(); setUserAction({ user: u, action }); }} />)}
                      </div>
                    )}
                  </div>
                )
              )}

              {/* ── All Contracts Tab ── */}
              {tab === 'allteams' && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12, alignItems: 'center' }}>
                    <div style={{ fontSize: 13, color: 'var(--slate-500)' }}>{visibleContracts.length} active contracts across all teams</div>
                  </div>
                  {visibleContracts.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 40, color: 'var(--slate-400)' }}>No active contracts</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {visibleContracts.map(c => (
                        <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', border: '1px solid var(--slate-200)', borderRadius: 'var(--r-md)', background: 'var(--white)' }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--navy-800)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.title || 'Untitled'}</div>
                            <div style={{ fontSize: 11.5, color: 'var(--slate-400)', marginTop: 2 }}>
                              {STAGES[c.stage]?.short} · {fmtMoney(c.budget)} · {c.analystName || 'Unassigned'}
                            </div>
                          </div>
                          <button className="btn btn-ghost btn-xs" onClick={() => setReassignTarget(c)}>
                            ⇄ Reassign
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ── Doc Profile Tab ── */}
              {tab === 'doctemplate' && (
                docProfileLoading ? (
                  <div style={{ textAlign: 'center', padding: 40, color: 'var(--slate-400)' }}>Loading profile…</div>
                ) : !docProfile?.agencyId ? (
                  <div style={{ textAlign: 'center', padding: 40, color: 'var(--slate-400)' }}>
                    <div style={{ fontSize: 28, marginBottom: 8 }}>⚠</div>
                    <div style={{ fontWeight: 600 }}>No agency context</div>
                    <div style={{ fontSize: 12, marginTop: 4 }}>This admin account isn't linked to a registered agency.</div>
                  </div>
                ) : (
                  <div>
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--navy-900)', marginBottom: 4 }}>Document Template Profile</div>
                      <div style={{ fontSize: 12, color: 'var(--slate-500)', lineHeight: 1.5 }}>
                        These settings control the default formatting for Penny's drafted exhibits at <strong>{profile.agencyName}</strong>. Used as the fallback when no specific template in the Contract Template Library matches the contract type at draft time.
                        {docProfile.raw
                          ? <span style={{ color: 'var(--green-700)', fontWeight: 600 }}> · Custom profile is active.</span>
                          : <span style={{ color: 'var(--slate-400)' }}> · Currently using app default.</span>}
                      </div>
                    </div>

                    {/* Friendly form */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 16 }}>
                      <div>
                        <label style={{ display: 'block', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--slate-500)', marginBottom: 4 }}>
                          Font Family
                        </label>
                        <select value={docProfileForm.fontFamily}
                          onChange={e => setDocProfileForm({ ...docProfileForm, fontFamily: e.target.value })}
                          style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1.5px solid var(--slate-200)', fontSize: 13 }}>
                          {['Arial','Calibri','Times New Roman','Cambria','Georgia','Helvetica','Verdana','Tahoma'].map(f =>
                            <option key={f} value={f}>{f}</option>)}
                        </select>
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--slate-500)', marginBottom: 4 }}>
                          Body Size (pt)
                        </label>
                        <input type="number" min="8" max="18" step="0.5"
                          value={docProfileForm.bodySizePt}
                          onChange={e => setDocProfileForm({ ...docProfileForm, bodySizePt: e.target.value })}
                          style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1.5px solid var(--slate-200)', fontSize: 13 }} />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--slate-500)', marginBottom: 4 }}>
                          Table Text Size (pt)
                        </label>
                        <input type="number" min="7" max="14" step="0.5"
                          value={docProfileForm.tableSizePt}
                          onChange={e => setDocProfileForm({ ...docProfileForm, tableSizePt: e.target.value })}
                          style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1.5px solid var(--slate-200)', fontSize: 13 }} />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--slate-500)', marginBottom: 4 }}>
                          Paragraph Spacing (pt)
                        </label>
                        <input type="number" min="0" max="20" step="0.5"
                          value={docProfileForm.spacingPt}
                          onChange={e => setDocProfileForm({ ...docProfileForm, spacingPt: e.target.value })}
                          style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1.5px solid var(--slate-200)', fontSize: 13 }} />
                      </div>
                      <div style={{ gridColumn: '1 / -1' }}>
                        <label style={{ display: 'block', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--slate-500)', marginBottom: 4 }}>
                          Agency Name in Body Text
                        </label>
                        <input type="text" placeholder="e.g., Energy Safety, CDFW, CalRecycle"
                          value={docProfileForm.agencyNameInText}
                          onChange={e => setDocProfileForm({ ...docProfileForm, agencyNameInText: e.target.value })}
                          style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1.5px solid var(--slate-200)', fontSize: 13 }} />
                        <div style={{ fontSize: 11, color: 'var(--slate-400)', marginTop: 4 }}>
                          Used in boilerplate (e.g. "the {docProfileForm.agencyNameInText || '<short name>'} Contract Manager will…")
                        </div>
                      </div>
                    </div>

                    {/* ── Drafting Voice: preferred / avoid phrases ─────────────────
                        Chip-style inputs. Reads from / writes to
                        agencies.doc_template.drafting_voice.{preferred,avoid}_phrases.
                        Penny's SOW draft and rewrite prompts pick these up via
                        getEffectiveProfile and inject them as a "LANGUAGE PREFERENCES"
                        rule. Empty arrays = no rule applied. */}
                    <div style={{ marginBottom: 18, padding: 14, background: 'var(--slate-50)', borderRadius: 8, border: '1px solid var(--slate-200)' }}>
                      <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--navy-700)', marginBottom: 4 }}>
                        Drafting voice: word preferences
                      </div>
                      <div style={{ fontSize: 11.5, color: 'var(--slate-500)', marginBottom: 14, lineHeight: 1.5 }}>
                        Tell Penny to prefer some words and avoid others. Applies to every Exhibit A draft and rewrite for this agency.
                        Type a word and press <kbd style={{ background: 'var(--slate-200)', padding: '1px 5px', borderRadius: 3, fontSize: 10 }}>Enter</kbd> or <kbd style={{ background: 'var(--slate-200)', padding: '1px 5px', borderRadius: 3, fontSize: 10 }}>,</kbd> to add. Click × to remove.
                      </div>

                      <PhraseChips
                        label="Avoid these words"
                        accent="#dc2626"
                        accentBg="#fee2e2"
                        accentBorder="#fca5a5"
                        placeholder='e.g., shall'
                        values={docProfileForm.avoidPhrases}
                        onChange={(next) => setDocProfileForm({ ...docProfileForm, avoidPhrases: next })}
                      />
                      <div style={{ height: 12 }} />
                      <PhraseChips
                        label="Prefer these instead"
                        accent="#15803d"
                        accentBg="#dcfce7"
                        accentBorder="#86efac"
                        placeholder='e.g., will, must'
                        values={docProfileForm.preferredPhrases}
                        onChange={(next) => setDocProfileForm({ ...docProfileForm, preferredPhrases: next })}
                      />
                    </div>

                    {/* ── PAAL Document ────────────────────────────────────
                        Upload the agency's Purchasing Authority Approval (PAAL)
                        document. Penny extracts the structured dollar thresholds
                        that determine whether the Exhibit A Term-section uses
                        DGS as the approver (over PAAL threshold) or the agency
                        itself (under threshold). Admin previews and edits the
                        extracted values before saving. The PDF stays in storage
                        as the source of truth. */}
                    <div style={{ marginBottom: 18, padding: 14, background: 'var(--slate-50)', borderRadius: 8, border: '1px solid var(--slate-200)' }}>
                      <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--navy-700)', marginBottom: 4 }}>
                        Purchasing Authority Approval (PAAL) Document
                      </div>
                      <div style={{ fontSize: 11.5, color: 'var(--slate-500)', marginBottom: 12, lineHeight: 1.5 }}>
                        Upload your agency's PAAL document (PDF). Penny will extract the dollar thresholds that determine when contracts require external approval — those values control the "approved by" language in every Exhibit A Term section.
                      </div>

                      {/* Current file status + upload button */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
                        {paalMeta ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', background: 'var(--white)', border: '1px solid var(--slate-200)', borderRadius: 6, fontSize: 12 }}>
                            <span style={{ color: 'var(--green-600)' }}>✓</span>
                            <span style={{ color: 'var(--slate-700)', fontWeight: 600 }}>{paalMeta.fileName}</span>
                            <button onClick={downloadPaalFile}
                              style={{ marginLeft: 4, padding: '2px 8px', borderRadius: 4, border: '1px solid var(--slate-300)', background: 'var(--white)', fontSize: 10.5, cursor: 'pointer', color: 'var(--slate-600)' }}>
                              Download
                            </button>
                          </div>
                        ) : (
                          <div style={{ fontSize: 12, color: 'var(--slate-400)', fontStyle: 'italic' }}>
                            No PAAL on file — defaults will apply ($50K Non-IT Services, $200K IT, DGS).
                          </div>
                        )}
                        <label style={{
                          padding: '6px 12px', borderRadius: 6, border: '1px solid var(--navy-700)',
                          background: 'var(--white)', fontSize: 12, fontWeight: 600,
                          cursor: paalExtracting ? 'wait' : 'pointer', color: 'var(--navy-700)',
                          opacity: paalExtracting ? 0.6 : 1,
                        }}>
                          {paalExtracting ? 'Extracting…' : (paalMeta ? '↻ Replace PAAL' : '⬆ Upload PAAL')}
                          <input type="file" accept="application/pdf"
                            onChange={handlePaalFileChange}
                            disabled={paalExtracting}
                            style={{ display: 'none' }} />
                        </label>
                      </div>

                      {/* Current saved values (when not in preview mode) */}
                      {paalValues && !paalPreview && (
                        <div style={{ padding: 12, background: 'var(--white)', border: '1px solid var(--slate-200)', borderRadius: 6, fontSize: 12 }}>
                          <div style={{ fontWeight: 700, color: 'var(--navy-800)', marginBottom: 8, fontSize: 11.5 }}>
                            Current Thresholds {paalValues.tier ? `(${paalValues.tier})` : ''}
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, color: 'var(--slate-600)' }}>
                            <div><strong>IT max:</strong> {fmtMoney(paalValues.it_self_approve_max)}</div>
                            <div><strong>Non-IT Goods max:</strong> {fmtMoney(paalValues.non_it_goods_self_approve_max)}</div>
                            <div><strong>Non-IT Services max:</strong> {fmtMoney(paalValues.non_it_services_self_approve_max)}</div>
                            <div><strong>Above threshold:</strong> {paalValues.external_approver}</div>
                          </div>
                          {paalValues.notes && (
                            <div style={{ marginTop: 8, padding: 8, background: 'var(--slate-50)', borderRadius: 4, fontSize: 11, color: 'var(--slate-500)', fontStyle: 'italic' }}>
                              {paalValues.notes}
                            </div>
                          )}
                          <button onClick={() => setPaalPreview({ ...paalValues })}
                            style={{ marginTop: 10, padding: '4px 10px', borderRadius: 5, border: '1px solid var(--slate-300)', background: 'var(--white)', fontSize: 11, cursor: 'pointer', color: 'var(--slate-600)' }}>
                            ✎ Edit values
                          </button>
                        </div>
                      )}

                      {/* Preview / Edit form — shown after upload-extraction
                          or when admin clicks Edit on existing values */}
                      {paalPreview && (
                        <div style={{ padding: 14, background: 'var(--blue-50, #eff6ff)', border: '1.5px solid #60a5fa', borderRadius: 6 }}>
                          <div style={{ fontWeight: 700, color: 'var(--navy-800)', marginBottom: 4, fontSize: 12 }}>
                            {paalValues ? 'Edit PAAL Thresholds' : 'Review Extracted Values'}
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--slate-500)', marginBottom: 12, lineHeight: 1.4 }}>
                            {paalValues
                              ? 'Hand-edit any value below and save.'
                              : 'Penny extracted these from the uploaded PAAL. Verify and edit before saving — these values drive Term-section language in every Exhibit A draft.'}
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                            <div>
                              <label style={{ display: 'block', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--slate-500)', marginBottom: 3 }}>
                                Tier
                              </label>
                              <input type="text" placeholder="e.g., Tier 1"
                                value={paalPreview.tier}
                                onChange={e => setPaalPreview({ ...paalPreview, tier: e.target.value })}
                                style={{ width: '100%', padding: '6px 8px', borderRadius: 5, border: '1.5px solid var(--slate-200)', fontSize: 12 }} />
                            </div>
                            <div>
                              <label style={{ display: 'block', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--slate-500)', marginBottom: 3 }}>
                                Approver Above Threshold
                              </label>
                              <input type="text"
                                value={paalPreview.external_approver}
                                onChange={e => setPaalPreview({ ...paalPreview, external_approver: e.target.value })}
                                style={{ width: '100%', padding: '6px 8px', borderRadius: 5, border: '1.5px solid var(--slate-200)', fontSize: 12 }} />
                            </div>
                            <div>
                              <label style={{ display: 'block', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--slate-500)', marginBottom: 3 }}>
                                IT Self-Approve Max ($)
                              </label>
                              <input type="number" min="0" step="1000"
                                value={paalPreview.it_self_approve_max}
                                onChange={e => setPaalPreview({ ...paalPreview, it_self_approve_max: e.target.value })}
                                style={{ width: '100%', padding: '6px 8px', borderRadius: 5, border: '1.5px solid var(--slate-200)', fontSize: 12 }} />
                            </div>
                            <div>
                              <label style={{ display: 'block', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--slate-500)', marginBottom: 3 }}>
                                Non-IT Goods Max ($)
                              </label>
                              <input type="number" min="0" step="1000"
                                value={paalPreview.non_it_goods_self_approve_max}
                                onChange={e => setPaalPreview({ ...paalPreview, non_it_goods_self_approve_max: e.target.value })}
                                style={{ width: '100%', padding: '6px 8px', borderRadius: 5, border: '1.5px solid var(--slate-200)', fontSize: 12 }} />
                            </div>
                            <div style={{ gridColumn: '1 / -1' }}>
                              <label style={{ display: 'block', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--slate-500)', marginBottom: 3 }}>
                                Non-IT Services Max ($) — use 0 if your PAAL shows N/A for Open Market
                              </label>
                              <input type="number" min="0" step="1000"
                                value={paalPreview.non_it_services_self_approve_max}
                                onChange={e => setPaalPreview({ ...paalPreview, non_it_services_self_approve_max: e.target.value })}
                                style={{ width: '100%', padding: '6px 8px', borderRadius: 5, border: '1.5px solid var(--slate-200)', fontSize: 12 }} />
                            </div>
                            <div style={{ gridColumn: '1 / -1' }}>
                              <label style={{ display: 'block', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--slate-500)', marginBottom: 3 }}>
                                Notes (exemptions, special cases, asterisked rules)
                              </label>
                              <textarea rows={3}
                                value={paalPreview.notes}
                                onChange={e => setPaalPreview({ ...paalPreview, notes: e.target.value })}
                                style={{ width: '100%', padding: '6px 8px', borderRadius: 5, border: '1.5px solid var(--slate-200)', fontSize: 12, fontFamily: 'inherit', resize: 'vertical' }} />
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                            <button onClick={savePaalPreview} disabled={paalSaving}
                              style={{ padding: '7px 16px', borderRadius: 6, border: 'none', background: 'linear-gradient(135deg,#16a34a,#22c55e)', fontSize: 12, fontWeight: 700, cursor: 'pointer', color: '#fff' }}>
                              {paalSaving ? 'Saving…' : '💾 Save Thresholds'}
                            </button>
                            <button onClick={() => setPaalPreview(null)} disabled={paalSaving}
                              style={{ padding: '7px 14px', borderRadius: 6, border: '1px solid var(--slate-200)', background: 'var(--white)', fontSize: 12, cursor: 'pointer', color: 'var(--slate-600)' }}>
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* ── Term Clause Configuration ───────────────────────
                        Shows the current Term-of-Performance sub-letters that
                        will be inserted as Section 2 in every Exhibit A.
                        Admin can review against agency templates (Penny finds
                        differences and proposes changes), edit text inline,
                        toggle sub-letters on/off, or reset to defaults. */}
                    <div style={{ marginBottom: 18, padding: 14, background: 'var(--slate-50)', borderRadius: 8, border: '1px solid var(--slate-200)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8, gap: 12 }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--navy-700)', marginBottom: 4 }}>
                            Term Clause (Section 2 of Exhibit A)
                          </div>
                          <div style={{ fontSize: 11.5, color: 'var(--slate-500)', lineHeight: 1.5 }}>
                            These sub-letters are inserted as the TERM OF PERFORMANCE section of every Exhibit A this agency drafts.
                            {termClause?.last_reviewed_at
                              ? <span style={{ color: 'var(--green-700)' }}> · Last reviewed {new Date(termClause.last_reviewed_at).toLocaleDateString()}.</span>
                              : <span style={{ color: 'var(--slate-400)' }}> · Using defaults — review against your templates to refine.</span>}
                          </div>
                        </div>
                        <button onClick={startTermReview} disabled={termReviewLoading}
                          style={{ padding: '7px 14px', borderRadius: 6, border: '1px solid var(--navy-700)', background: 'var(--white)', fontSize: 12, fontWeight: 600, cursor: 'pointer', color: 'var(--navy-700)', whiteSpace: 'nowrap' }}>
                          {termReviewLoading ? 'Reviewing…' : '🔍 Review Term Clause'}
                        </button>
                      </div>

                      {/* Sub-letter list */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
                        {(termSubletters || []).map((s, i) => {
                          const label = String.fromCharCode(65 + i);
                          const inactive = s.active === false;
                          const isEditing = termEditing?.idx === i;
                          return (
                            <div key={s.id + '-' + i} style={{
                              padding: '10px 12px',
                              background: inactive ? 'var(--slate-100)' : 'var(--white)',
                              border: '1px solid var(--slate-200)',
                              borderRadius: 6,
                              opacity: inactive ? 0.6 : 1,
                            }}>
                              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                                <span style={{
                                  flexShrink: 0, fontSize: 11.5, fontWeight: 700,
                                  color: 'var(--navy-700)', padding: '2px 8px',
                                  background: 'var(--slate-100)', borderRadius: 99,
                                  minWidth: 24, textAlign: 'center',
                                }}>{label}</span>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  {isEditing ? (
                                    <div>
                                      <textarea
                                        defaultValue={s.text}
                                        rows={3}
                                        autoFocus
                                        id={`term-edit-${i}`}
                                        style={{
                                          width: '100%', padding: '6px 8px', borderRadius: 5,
                                          border: '1.5px solid #60a5fa', fontSize: 12,
                                          fontFamily: 'inherit', resize: 'vertical',
                                        }} />
                                      <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                                        <button onClick={() => {
                                          const el = document.getElementById(`term-edit-${i}`);
                                          saveEditedSubletter(i, el?.value || s.text);
                                        }}
                                          style={{ padding: '4px 10px', borderRadius: 5, border: 'none', background: 'var(--navy-700)', color: '#fff', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                                          Save
                                        </button>
                                        <button onClick={() => setTermEditing(null)}
                                          style={{ padding: '4px 10px', borderRadius: 5, border: '1px solid var(--slate-200)', background: 'var(--white)', fontSize: 11, cursor: 'pointer', color: 'var(--slate-600)' }}>
                                          Cancel
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    <>
                                      <div style={{ fontSize: 12, color: 'var(--navy-800)', lineHeight: 1.55 }}>
                                        {s.text}
                                      </div>
                                      {s.source && s.source !== 'default' && (
                                        <div style={{ fontSize: 10, marginTop: 4, color: 'var(--slate-400)', fontStyle: 'italic' }}>
                                          {s.source === 'penny_recommendation' ? 'Added by Penny recommendation' : 'Manually added'}
                                        </div>
                                      )}
                                    </>
                                  )}
                                </div>
                                {!isEditing && (
                                  <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                                    <button onClick={() => setTermEditing({ idx: i })}
                                      title="Edit text"
                                      style={{ padding: '3px 7px', borderRadius: 4, border: '1px solid var(--slate-200)', background: 'var(--white)', fontSize: 10, cursor: 'pointer', color: 'var(--slate-500)' }}>
                                      ✎
                                    </button>
                                    <button onClick={() => toggleTermSubletter(i)}
                                      title={inactive ? 'Enable' : 'Disable'}
                                      style={{ padding: '3px 7px', borderRadius: 4, border: '1px solid var(--slate-200)', background: 'var(--white)', fontSize: 10, cursor: 'pointer', color: inactive ? 'var(--green-600)' : 'var(--slate-500)' }}>
                                      {inactive ? '○' : '●'}
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Reset button — only show if non-default */}
                      {termClause?.subletters && (
                        <button onClick={resetTermClause}
                          style={{ marginTop: 10, padding: '4px 10px', borderRadius: 5, border: '1px solid var(--red-200)', background: 'var(--red-50)', fontSize: 11, cursor: 'pointer', color: 'var(--red-600)' }}>
                          Reset Term Clause to Defaults
                        </button>
                      )}

                      <div style={{ marginTop: 10, padding: 8, background: 'var(--slate-100)', borderRadius: 4, fontSize: 10.5, color: 'var(--slate-500)', lineHeight: 1.5 }}>
                        <strong>Placeholders:</strong> The docx builder substitutes <code>{'{start_date}'}</code>, <code>{'{end_date}'}</code>, <code>{'{approver}'}</code>, <code>{'{agency_short_name}'}</code>, <code>{'{contract_title}'}</code>, and <code>{'{contractor_name}'}</code> at draft time. Preserve these tokens when editing.
                      </div>
                    </div>

                    {/* ── Exhibit B Template ─────────────────────────────
                        Agency-level baseline for Exhibit B (Budget Detail and
                        Payment Provisions). Penny extracts canonical sections
                        from the agency's templates; admin reviews/edits/saves.
                        Per-contract drafts then customize this baseline rather
                        than synthesizing from scratch — gives consistency. */}
                    <div style={{ marginBottom: 18, padding: 14, background: 'var(--slate-50)', borderRadius: 8, border: '1px solid var(--slate-200)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8, gap: 12 }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--navy-700)', marginBottom: 4 }}>
                            Exhibit B Template (Budget Detail and Payment Provisions)
                          </div>
                          <div style={{ fontSize: 11.5, color: 'var(--slate-500)', lineHeight: 1.5 }}>
                            {exhibitBTemplate?.sections?.length > 0
                              ? <>Saved baseline that Penny customizes per-contract. {exhibitBTemplate.last_reviewed_at && <span style={{ color: 'var(--green-700)' }}>Last extracted {new Date(exhibitBTemplate.last_reviewed_at).toLocaleDateString()}.</span>}</>
                              : <span style={{ color: 'var(--slate-400)' }}>Not yet configured. Penny will synthesize Exhibit B from templates on each draft until you extract a baseline here.</span>}
                          </div>
                        </div>
                        <button onClick={startExhibitBReview} disabled={exbReviewLoading}
                          style={{ padding: '7px 14px', borderRadius: 6, border: '1px solid var(--navy-700)', background: 'var(--white)', fontSize: 12, fontWeight: 600, cursor: 'pointer', color: 'var(--navy-700)', whiteSpace: 'nowrap' }}>
                          {exbReviewLoading ? 'Extracting…' : (exhibitBTemplate ? '↻ Re-extract' : '🔍 Extract from Templates')}
                        </button>
                      </div>

                      {/* Section list when saved */}
                      {exbSections.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
                          {exbSections.map((s, i) => {
                            const inactive = s.active === false;
                            const isEditing = exbEditing?.idx === i;
                            return (
                              <div key={s.id + '-' + i} style={{
                                padding: '10px 12px',
                                background: inactive ? 'var(--slate-100)' : 'var(--white)',
                                border: '1px solid var(--slate-200)',
                                borderRadius: 6,
                                opacity: inactive ? 0.6 : 1,
                              }}>
                                {isEditing ? (
                                  <div>
                                    <input type="text" id={`exb-heading-${i}`} defaultValue={s.heading}
                                      placeholder="Section heading (e.g. 1. PAYMENT SCHEDULE)"
                                      style={{ width: '100%', padding: '6px 8px', borderRadius: 5, border: '1.5px solid #60a5fa', fontSize: 12, fontWeight: 700, color: 'var(--navy-800)', marginBottom: 6 }} />
                                    <textarea id={`exb-text-${i}`} defaultValue={s.text} rows={6} autoFocus
                                      style={{ width: '100%', padding: '6px 8px', borderRadius: 5, border: '1.5px solid #60a5fa', fontSize: 12, fontFamily: 'inherit', resize: 'vertical', lineHeight: 1.5 }} />
                                    <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                                      <button onClick={() => {
                                        const h = document.getElementById(`exb-heading-${i}`)?.value || s.heading;
                                        const t = document.getElementById(`exb-text-${i}`)?.value || s.text;
                                        saveEditedExbSection(i, h, t);
                                      }} style={{ padding: '4px 10px', borderRadius: 5, border: 'none', background: 'var(--navy-700)', color: '#fff', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>Save</button>
                                      <button onClick={() => setExbEditing(null)}
                                        style={{ padding: '4px 10px', borderRadius: 5, border: '1px solid var(--slate-200)', background: 'var(--white)', fontSize: 11, cursor: 'pointer', color: 'var(--slate-600)' }}>Cancel</button>
                                    </div>
                                  </div>
                                ) : (
                                  <div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                                      <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--navy-800)', flex: 1 }}>
                                        {s.heading}
                                      </div>
                                      <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                                        <button onClick={() => setExbEditing({ idx: i })} title="Edit"
                                          style={{ padding: '3px 7px', borderRadius: 4, border: '1px solid var(--slate-200)', background: 'var(--white)', fontSize: 10, cursor: 'pointer', color: 'var(--slate-500)' }}>✎</button>
                                        <button onClick={() => toggleExbSection(i)} title={inactive ? 'Enable' : 'Disable'}
                                          style={{ padding: '3px 7px', borderRadius: 4, border: '1px solid var(--slate-200)', background: 'var(--white)', fontSize: 10, cursor: 'pointer', color: inactive ? 'var(--green-600)' : 'var(--slate-500)' }}>{inactive ? '○' : '●'}</button>
                                      </div>
                                    </div>
                                    <div style={{ fontSize: 11, color: 'var(--slate-600)', lineHeight: 1.55, marginTop: 4, whiteSpace: 'pre-wrap' }}>
                                      {s.text}
                                    </div>
                                    {s.source && s.source !== 'default' && (
                                      <div style={{ fontSize: 10, marginTop: 4, color: 'var(--slate-400)', fontStyle: 'italic' }}>
                                        {s.source === 'penny_recommendation' ? 'Extracted by Penny' : 'Manually edited'}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {exbSections.length > 0 && (
                        <button onClick={resetExhibitBTemplate}
                          style={{ marginTop: 10, padding: '4px 10px', borderRadius: 5, border: '1px solid var(--red-200)', background: 'var(--red-50)', fontSize: 11, cursor: 'pointer', color: 'var(--red-600)' }}>
                          Clear Exhibit B Template
                        </button>
                      )}

                      <div style={{ marginTop: 10, padding: 8, background: 'var(--slate-100)', borderRadius: 4, fontSize: 10.5, color: 'var(--slate-500)', lineHeight: 1.5 }}>
                        <strong>Placeholders:</strong> <code>{'{contract_total}'}</code>, <code>{'{contractor_name}'}</code>, <code>{'{agreement_num}'}</code>, <code>{'{contract_title}'}</code>, <code>{'{agency_short_name}'}</code>, <code>{'{net_terms_days}'}</code>, <code>{'{invoicing_address}'}</code>, <code>{'{end_date}'}</code>, and <code>{'{payment_schedule}'}</code> are substituted at draft time.
                      </div>
                    </div>

                    {/* ── Exhibit D Template ─────────────────────────────
                        Same pattern as Exhibit B but for Special Terms and
                        Conditions. Penny extracts canonical sections from
                        templates; per-contract drafts customize the baseline. */}
                    <div style={{ marginBottom: 18, padding: 14, background: 'var(--slate-50)', borderRadius: 8, border: '1px solid var(--slate-200)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8, gap: 12 }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--navy-700)', marginBottom: 4 }}>
                            Exhibit D Template (Special Terms and Conditions)
                          </div>
                          <div style={{ fontSize: 11.5, color: 'var(--slate-500)', lineHeight: 1.5 }}>
                            {exhibitDTemplate?.sections?.length > 0
                              ? <>Saved baseline that Penny customizes per-contract. {exhibitDTemplate.last_reviewed_at && <span style={{ color: 'var(--green-700)' }}>Last extracted {new Date(exhibitDTemplate.last_reviewed_at).toLocaleDateString()}.</span>}</>
                              : <span style={{ color: 'var(--slate-400)' }}>Not yet configured. Penny will synthesize Exhibit D from templates on each draft until you extract a baseline here.</span>}
                          </div>
                        </div>
                        <button onClick={startExhibitDReview} disabled={exdReviewLoading}
                          style={{ padding: '7px 14px', borderRadius: 6, border: '1px solid var(--navy-700)', background: 'var(--white)', fontSize: 12, fontWeight: 600, cursor: 'pointer', color: 'var(--navy-700)', whiteSpace: 'nowrap' }}>
                          {exdReviewLoading ? 'Extracting…' : (exhibitDTemplate ? '↻ Re-extract' : '🔍 Extract from Templates')}
                        </button>
                      </div>

                      {/* Section list when saved */}
                      {exdSections.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
                          {exdSections.map((s, i) => {
                            const inactive = s.active === false;
                            const isEditing = exdEditing?.idx === i;
                            return (
                              <div key={s.id + '-' + i} style={{
                                padding: '10px 12px',
                                background: inactive ? 'var(--slate-100)' : 'var(--white)',
                                border: '1px solid var(--slate-200)',
                                borderRadius: 6,
                                opacity: inactive ? 0.6 : 1,
                              }}>
                                {isEditing ? (
                                  <div>
                                    <input type="text" id={`exd-heading-${i}`} defaultValue={s.heading}
                                      placeholder="Section heading (e.g. 1. TERMINATION FOR CONVENIENCE)"
                                      style={{ width: '100%', padding: '6px 8px', borderRadius: 5, border: '1.5px solid #60a5fa', fontSize: 12, fontWeight: 700, color: 'var(--navy-800)', marginBottom: 6 }} />
                                    <textarea id={`exd-text-${i}`} defaultValue={s.text} rows={6} autoFocus
                                      style={{ width: '100%', padding: '6px 8px', borderRadius: 5, border: '1.5px solid #60a5fa', fontSize: 12, fontFamily: 'inherit', resize: 'vertical', lineHeight: 1.5 }} />
                                    <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                                      <button onClick={() => {
                                        const h = document.getElementById(`exd-heading-${i}`)?.value || s.heading;
                                        const t = document.getElementById(`exd-text-${i}`)?.value || s.text;
                                        saveEditedExdSection(i, h, t);
                                      }} style={{ padding: '4px 10px', borderRadius: 5, border: 'none', background: 'var(--navy-700)', color: '#fff', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>Save</button>
                                      <button onClick={() => setExdEditing(null)}
                                        style={{ padding: '4px 10px', borderRadius: 5, border: '1px solid var(--slate-200)', background: 'var(--white)', fontSize: 11, cursor: 'pointer', color: 'var(--slate-600)' }}>Cancel</button>
                                    </div>
                                  </div>
                                ) : (
                                  <div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                                      <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--navy-800)', flex: 1 }}>
                                        {s.heading}
                                      </div>
                                      <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                                        <button onClick={() => setExdEditing({ idx: i })} title="Edit"
                                          style={{ padding: '3px 7px', borderRadius: 4, border: '1px solid var(--slate-200)', background: 'var(--white)', fontSize: 10, cursor: 'pointer', color: 'var(--slate-500)' }}>✎</button>
                                        <button onClick={() => toggleExdSection(i)} title={inactive ? 'Enable' : 'Disable'}
                                          style={{ padding: '3px 7px', borderRadius: 4, border: '1px solid var(--slate-200)', background: 'var(--white)', fontSize: 10, cursor: 'pointer', color: inactive ? 'var(--green-600)' : 'var(--slate-500)' }}>{inactive ? '○' : '●'}</button>
                                      </div>
                                    </div>
                                    <div style={{ fontSize: 11, color: 'var(--slate-600)', lineHeight: 1.55, marginTop: 4, whiteSpace: 'pre-wrap' }}>
                                      {s.text}
                                    </div>
                                    {s.source && s.source !== 'default' && (
                                      <div style={{ fontSize: 10, marginTop: 4, color: 'var(--slate-400)', fontStyle: 'italic' }}>
                                        {s.source === 'penny_recommendation' ? 'Extracted by Penny' : 'Manually edited'}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {exdSections.length > 0 && (
                        <button onClick={resetExhibitDTemplate}
                          style={{ marginTop: 10, padding: '4px 10px', borderRadius: 5, border: '1px solid var(--red-200)', background: 'var(--red-50)', fontSize: 11, cursor: 'pointer', color: 'var(--red-600)' }}>
                          Clear Exhibit D Template
                        </button>
                      )}

                      <div style={{ marginTop: 10, padding: 8, background: 'var(--slate-100)', borderRadius: 4, fontSize: 10.5, color: 'var(--slate-500)', lineHeight: 1.5 }}>
                        <strong>Placeholders:</strong> <code>{'{contractor_name}'}</code>, <code>{'{agreement_num}'}</code>, <code>{'{contract_title}'}</code>, <code>{'{agency_short_name}'}</code>, <code>{'{end_date}'}</code>. The docx builder substitutes these at draft time.
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
                      <button onClick={saveDocTemplateForm} disabled={docProfileSaving}
                        style={{ padding: '8px 18px', borderRadius: 7, border: 'none', background: 'linear-gradient(135deg,#16a34a,#22c55e)', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', color: '#fff' }}>
                        {docProfileSaving ? 'Saving…' : '💾 Save Profile'}
                      </button>
                      <button onClick={() => setShowAdvancedJson(!showAdvancedJson)}
                        style={{ padding: '8px 14px', borderRadius: 7, border: '1px solid var(--slate-200)', background: 'var(--slate-50)', fontSize: 12, cursor: 'pointer', color: 'var(--slate-600)' }}>
                        {showAdvancedJson ? '▲ Hide' : '▼ Show'} Advanced (raw JSON)
                      </button>
                      {docProfile.raw && (
                        <button onClick={clearDocTemplate} disabled={docProfileSaving}
                          style={{ padding: '8px 14px', borderRadius: 7, border: '1px solid var(--red-200)', background: 'var(--red-50)', fontSize: 12, cursor: 'pointer', color: 'var(--red-600)' }}>
                          Reset to App Default
                        </button>
                      )}
                    </div>

                    {/* Advanced raw JSON editor */}
                    {showAdvancedJson && (
                      <div style={{ marginTop: 8, padding: 14, background: 'var(--slate-50)', borderRadius: 8, border: '1px solid var(--slate-200)' }}>
                        <div style={{ fontSize: 11, color: 'var(--slate-500)', marginBottom: 8, lineHeight: 1.5 }}>
                          Edit the full profile JSON directly. This includes fields not exposed in the friendly form (page header lines, exhibit titles, table colors, section schema, etc). Saving here overwrites the entire profile — fields you omit fall back to the app default.
                        </div>
                        <textarea
                          value={advancedJson}
                          onChange={e => setAdvancedJson(e.target.value)}
                          spellCheck={false}
                          rows={16}
                          style={{
                            width: '100%', fontFamily: 'monospace', fontSize: 11.5,
                            padding: 12, borderRadius: 6, border: '1.5px solid var(--slate-200)',
                            background: 'var(--white)', resize: 'vertical',
                          }}
                        />
                        {advancedJsonError && (
                          <div style={{ fontSize: 11, color: 'var(--red-600)', marginTop: 6, fontWeight: 600 }}>
                            ⚠ {advancedJsonError}
                          </div>
                        )}
                        <div style={{ marginTop: 10 }}>
                          <button onClick={saveAdvancedJson} disabled={docProfileSaving}
                            style={{ padding: '6px 14px', borderRadius: 6, border: 'none', background: 'var(--navy-700)', fontSize: 12, fontWeight: 700, cursor: 'pointer', color: '#fff' }}>
                            {docProfileSaving ? 'Saving…' : 'Save Raw JSON'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )
              )}

              {/* ── Clause Library Tab ── */}
              {tab === 'clauses' && (
                docProfileLoading ? (
                  <div style={{ textAlign: 'center', padding: 40, color: 'var(--slate-400)' }}>Loading…</div>
                ) : !docProfile?.agencyId ? (
                  <div style={{ textAlign: 'center', padding: 40, color: 'var(--slate-400)' }}>
                    <div style={{ fontSize: 28, marginBottom: 8 }}>⚠</div>
                    <div style={{ fontWeight: 600 }}>No agency context</div>
                  </div>
                ) : (
                  <div>
                    <div style={{ marginBottom: 20 }}>
                      <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy-800)', marginBottom: 4 }}>🔒 Clause Library</div>
                      <div style={{ fontSize: 12.5, color: 'var(--slate-500)', lineHeight: 1.6 }}>
                        Define approved language for specific sections in each exhibit. <strong>Locked clauses</strong> are injected verbatim by Penny and cannot be edited by analysts. <strong>Reference clauses</strong> guide Penny but allow her to adapt. <strong>Required clauses</strong> are always included.
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '2px solid var(--slate-100)', paddingBottom: 8 }}>
                      {[
                        { key: 'A', label: 'Exhibit A — SOW' },
                        { key: 'B', label: 'Exhibit B — Budget & Payment' },
                        { key: 'D', label: 'Exhibit D — Special Terms' },
                        { key: 'Sol', label: 'Solicitation' },
                      ].map(et => (
                        <button key={et.key} onClick={() => setClauseTab(et.key)} style={{
                          padding: '6px 14px', borderRadius: 'var(--r-md)', fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
                          background: clauseTab === et.key ? 'var(--navy-800)' : 'transparent',
                          color: clauseTab === et.key ? 'white' : 'var(--slate-500)',
                          border: 'none',
                        }}>
                          {et.label}
                          {(clauseLibrary[et.key]?.length > 0) && (
                            <span style={{ marginLeft: 6, fontSize: 10, padding: '1px 5px', borderRadius: 99, background: clauseTab === et.key ? 'rgba(255,255,255,.2)' : 'var(--slate-200)', color: clauseTab === et.key ? 'white' : 'var(--slate-500)' }}>
                              {clauseLibrary[et.key].length}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>

                    {(() => {
                      const clauses = clauseLibrary[clauseTab] || [];
                      return (
                        <div>
                          {clauses.length === 0 && clauseAdding !== clauseTab && (
                            <div style={{ textAlign: 'center', padding: '32px 16px', borderRadius: 'var(--r-lg)', border: '2px dashed var(--slate-200)', color: 'var(--slate-400)', fontSize: 13, marginBottom: 16 }}>
                              No clauses defined for this exhibit yet.<br />
                              <span style={{ fontSize: 12 }}>Add your first approved clause below.</span>
                            </div>
                          )}

                          {clauses.map((clause, idx) => (
                            <div key={clause.id || idx} style={{ marginBottom: 12, padding: '14px 16px', borderRadius: 'var(--r-md)', border: '1.5px solid ' + (clause.locked ? 'var(--navy-200)' : 'var(--slate-200)'), background: clause.locked ? 'var(--navy-50)' : 'white' }}>
                              {clauseEditing?.exhibit === clauseTab && clauseEditing?.idx === idx ? (
                                <div>
                                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                                    <div>
                                      <div style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--slate-600)', marginBottom: 4 }}>Section Name</div>
                                      <input value={clauseEditForm.sectionName} onChange={e => setClauseEditForm(p => ({ ...p, sectionName: e.target.value }))}
                                        style={{ width: '100%', padding: '7px 10px', borderRadius: 'var(--r-md)', border: '1.5px solid var(--slate-200)', fontSize: 13, boxSizing: 'border-box' }} />
                                    </div>
                                    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', paddingBottom: 4 }}>
                                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, cursor: 'pointer' }}>
                                        <input type="checkbox" checked={clauseEditForm.locked} onChange={e => setClauseEditForm(p => ({ ...p, locked: e.target.checked }))} />
                                        🔒 Locked
                                      </label>
                                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, cursor: 'pointer' }}>
                                        <input type="checkbox" checked={clauseEditForm.required} onChange={e => setClauseEditForm(p => ({ ...p, required: e.target.checked }))} />
                                        Required
                                      </label>
                                    </div>
                                  </div>
                                  <div style={{ marginBottom: 10 }}>
                                    <div style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--slate-600)', marginBottom: 4 }}>Approved Language</div>
                                    <textarea value={clauseEditForm.language} onChange={e => setClauseEditForm(p => ({ ...p, language: e.target.value }))}
                                      rows={5} style={{ width: '100%', padding: '8px 10px', borderRadius: 'var(--r-md)', border: '1.5px solid var(--slate-200)', fontSize: 12.5, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }} />
                                  </div>
                                  <div style={{ display: 'flex', gap: 8 }}>
                                    <button className="btn btn-gold btn-sm" disabled={clauseSaving || !clauseEditForm.sectionName.trim() || !clauseEditForm.language.trim()}
                                      onClick={() => updateClause(clauseTab, idx, { sectionName: clauseEditForm.sectionName, language: clauseEditForm.language, locked: clauseEditForm.locked, required: clauseEditForm.required })}>
                                      {clauseSaving ? 'Saving…' : 'Save'}
                                    </button>
                                    <button className="btn btn-ghost btn-sm" onClick={() => setClauseEditing(null)}>Cancel</button>
                                  </div>
                                </div>
                              ) : (
                                <div>
                                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 4 }}>
                                    <div style={{ flex: 1 }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy-800)' }}>{clause.sectionName}</span>
                                        {clause.locked && <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 99, background: 'var(--navy-800)', color: 'white', fontWeight: 700 }}>🔒 Locked</span>}
                                        {clause.required && <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 99, background: 'var(--gold-100)', color: 'var(--gold-800)', fontWeight: 700, border: '1px solid var(--gold-300)' }}>Required</span>}
                                        {!clause.locked && <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 99, background: 'var(--slate-100)', color: 'var(--slate-500)' }}>Reference</span>}
                                      </div>
                                      <div style={{ fontSize: 12.5, color: 'var(--slate-600)', lineHeight: 1.65, whiteSpace: 'pre-wrap', maxHeight: 80, overflowY: 'auto', padding: '6px 8px', background: 'rgba(0,0,0,.03)', borderRadius: 4 }}>
                                        {clause.language}
                                      </div>
                                    </div>
                                    <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                                      <button className="btn btn-ghost btn-xs" title={clause.locked ? 'Unlock this clause' : 'Lock this clause'} onClick={() => toggleClauseLock(clauseTab, idx)}>
                                        {clause.locked ? '🔓 Unlock' : '🔒 Lock'}
                                      </button>
                                      <button className="btn btn-ghost btn-xs" onClick={() => { setClauseEditing({ exhibit: clauseTab, idx }); setClauseEditForm({ sectionName: clause.sectionName, language: clause.language, locked: clause.locked, required: clause.required }); }}>
                                        Edit
                                      </button>
                                      <button className="btn btn-ghost btn-xs" style={{ color: 'var(--red-400)' }} onClick={() => deleteClause(clauseTab, idx)}>
                                        Delete
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}

                          {clauseAdding === clauseTab ? (
                            <div style={{ padding: '14px 16px', borderRadius: 'var(--r-md)', border: '1.5px dashed var(--gold-300)', background: 'var(--gold-50)', marginTop: 8 }}>
                              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy-700)', marginBottom: 12 }}>New Clause</div>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                                <div>
                                  <div style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--slate-600)', marginBottom: 4 }}>Section Name</div>
                                  <input placeholder="e.g. Conflict of Interest" value={clauseEditForm.sectionName} onChange={e => setClauseEditForm(p => ({ ...p, sectionName: e.target.value }))}
                                    style={{ width: '100%', padding: '7px 10px', borderRadius: 'var(--r-md)', border: '1.5px solid var(--slate-200)', fontSize: 13, boxSizing: 'border-box' }} />
                                </div>
                                <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', paddingBottom: 4 }}>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, cursor: 'pointer' }}>
                                    <input type="checkbox" checked={clauseEditForm.locked} onChange={e => setClauseEditForm(p => ({ ...p, locked: e.target.checked }))} />
                                    🔒 Locked
                                  </label>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, cursor: 'pointer' }}>
                                    <input type="checkbox" checked={clauseEditForm.required} onChange={e => setClauseEditForm(p => ({ ...p, required: e.target.checked }))} />
                                    Required
                                  </label>
                                </div>
                              </div>
                              <div style={{ marginBottom: 10 }}>
                                <div style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--slate-600)', marginBottom: 4 }}>Approved Language</div>
                                <textarea placeholder="Enter the exact approved language for this section…" value={clauseEditForm.language} onChange={e => setClauseEditForm(p => ({ ...p, language: e.target.value }))}
                                  rows={5} style={{ width: '100%', padding: '8px 10px', borderRadius: 'var(--r-md)', border: '1.5px solid var(--slate-200)', fontSize: 12.5, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }} />
                              </div>
                              <div style={{ display: 'flex', gap: 8 }}>
                                <button className="btn btn-gold btn-sm" disabled={clauseSaving || !clauseEditForm.sectionName.trim() || !clauseEditForm.language.trim()} onClick={() => addClause(clauseTab)}>
                                  {clauseSaving ? 'Saving…' : '+ Add Clause'}
                                </button>
                                <button className="btn btn-ghost btn-sm" onClick={() => { setClauseAdding(null); setClauseEditForm({ sectionName: '', language: '', locked: true, required: false }); }}>Cancel</button>
                              </div>
                            </div>
                          ) : (
                            <button className="btn btn-ghost btn-sm" style={{ marginTop: 8 }} onClick={() => { setClauseAdding(clauseTab); setClauseEditForm({ sectionName: '', language: '', locked: true, required: false }); }}>
                              + Add Clause
                            </button>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 24px', borderTop: '1px solid var(--slate-100)', fontSize: 11.5, color: 'var(--slate-400)' }}>
          {isAgencySuperAdmin ? 'Agency Division Chief — full visibility across all teams and contracts.' : 'Supervisor — manage your team and pending approvals.'}
        </div>
      </div>

      {/* User Action Modal */}
      {userAction && (
        <UserActionModal
          action={userAction.action}
          user={userAction.user}
          analysts={analysts}
          supervisors={supervisors}
          onChangeRole={changeUserRole}
          onChangeTeam={changeUserTeam}
          onDeactivate={deactivateUser}
          onReinstate={reinstateUser}
          onClose={() => setUserAction(null)}
          working={working[userAction.user?.id]}
        />
      )}

      {/* Reassign Modal */}
      {reassignTarget && (
        <ReassignModal
          contract={reassignTarget}
          analysts={analysts}
          onReassign={reassignContract}
          onClose={() => setReassignTarget(null)}
        />
      )}

      {/* Term Clause Review Modal */}
      {termReviewOpen && (
        <TermReviewModal
          loading={termReviewLoading}
          findings={termFindings}
          checked={termFindingsChecked}
          onToggle={(id) => setTermFindingsChecked(c => ({ ...c, [id]: !c[id] }))}
          onApply={applyTermFindings}
          onClose={() => { setTermReviewOpen(false); setTermFindings([]); setTermFindingsChecked({}); }}
          applying={termApplying}
        />
      )}

      {/* Exhibit B Template Extraction Modal */}
      {exbReviewOpen && (
        <ExhibitBExtractModal
          loading={exbReviewLoading}
          sections={exbExtracted}
          onUpdate={updateExtractedSection}
          onRemove={removeExtractedSection}
          onSave={saveExhibitBTemplate}
          onClose={() => { setExbReviewOpen(false); setExbExtracted(null); }}
          saving={exbSaving}
        />
      )}

      {/* Exhibit D Template Extraction Modal — reuses the same component */}
      {exdReviewOpen && (
        <ExhibitBExtractModal
          exhibit="D"
          loading={exdReviewLoading}
          sections={exdExtracted}
          onUpdate={updateExtractedDSection}
          onRemove={removeExtractedDSection}
          onSave={saveExhibitDTemplate}
          onClose={() => { setExdReviewOpen(false); setExdExtracted(null); }}
          saving={exdSaving}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// TermReviewModal — full-screen overlay for the Term Clause Review flow.
//
// Phases:
//   1. Loading (Penny is analyzing the templates).
//   2. Findings list — each shows title + rationale + checkbox + preview of
//      the change. User checks the ones to apply.
//   3. After "Apply Selected", the parent calls applyTermFindings and closes
//      the modal on success.
// ─────────────────────────────────────────────────────────────────────
function TermReviewModal({ loading, findings, checked, onToggle, onApply, onClose, applying }) {
  const checkedCount = findings.filter(f => checked[f.id]).length;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: 24 }}>
      <div style={{ background: 'var(--white)', borderRadius: 'var(--r-xl, 16px)', width: '100%', maxWidth: 720, maxHeight: '88vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px rgba(0,0,0,0.3)' }}>
        <div style={{ padding: '18px 24px', borderBottom: '1px solid var(--slate-100)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--navy-900)' }}>Term Clause Review</div>
            <div style={{ fontSize: 12, color: 'var(--slate-400)', marginTop: 2 }}>
              Penny compared your current Term clause against agency templates.
            </div>
          </div>
          <button onClick={onClose} disabled={applying} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: 'var(--slate-300)' }}>×</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 60, color: 'var(--slate-500)' }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>🔍</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--navy-700)' }}>Penny is analyzing your templates…</div>
              <div style={{ fontSize: 12, color: 'var(--slate-400)', marginTop: 6 }}>This usually takes 15–30 seconds.</div>
            </div>
          ) : findings.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 60, color: 'var(--slate-500)' }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>✓</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--navy-800)' }}>No changes recommended</div>
              <div style={{ fontSize: 12, color: 'var(--slate-500)', marginTop: 6, lineHeight: 1.5 }}>
                Your current Term clause matches the patterns in your agency templates. No updates needed.
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ fontSize: 12, color: 'var(--slate-500)', marginBottom: 4 }}>
                Penny found <strong>{findings.length}</strong> {findings.length === 1 ? 'recommendation' : 'recommendations'}. Check the ones you want to apply.
              </div>
              {findings.map(f => {
                const isChecked = !!checked[f.id];
                const sevColor = f.severity === 'recommended' ? '#16a34a' : '#0284c7';
                const sevBg    = f.severity === 'recommended' ? '#dcfce7' : '#e0f2fe';
                const sevLabel = f.severity === 'recommended' ? 'RECOMMENDED' : 'SUGGESTION';
                return (
                  <label key={f.id} style={{
                    display: 'flex', alignItems: 'flex-start', gap: 12, padding: 14,
                    background: isChecked ? '#f0fdf4' : 'var(--white)',
                    border: '1.5px solid ' + (isChecked ? '#86efac' : 'var(--slate-200)'),
                    borderRadius: 8, cursor: 'pointer',
                  }}>
                    <input type="checkbox" checked={isChecked} onChange={() => onToggle(f.id)}
                      style={{ marginTop: 2, width: 16, height: 16, cursor: 'pointer', flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <span style={{ fontSize: 9.5, fontWeight: 700, color: sevColor, background: sevBg, padding: '2px 7px', borderRadius: 99, letterSpacing: '.04em' }}>
                          {sevLabel}
                        </span>
                        <span style={{ fontSize: 10.5, color: 'var(--slate-400)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em' }}>
                          {f.type === 'missing_subletter' ? 'New sub-letter' : 'Wording change'}
                        </span>
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy-800)', marginBottom: 4 }}>{f.title}</div>
                      <div style={{ fontSize: 12, color: 'var(--slate-600)', marginBottom: 8, lineHeight: 1.5 }}>{f.rationale}</div>
                      {f.type === 'wording_difference' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
                          <div style={{ padding: '8px 10px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 5, fontSize: 11.5, lineHeight: 1.5 }}>
                            <div style={{ fontSize: 9.5, fontWeight: 700, color: '#dc2626', marginBottom: 3, letterSpacing: '.04em' }}>CURRENT</div>
                            <div style={{ color: 'var(--slate-700)' }}>{f.current_text}</div>
                          </div>
                          <div style={{ padding: '8px 10px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 5, fontSize: 11.5, lineHeight: 1.5 }}>
                            <div style={{ fontSize: 9.5, fontWeight: 700, color: '#16a34a', marginBottom: 3, letterSpacing: '.04em' }}>PROPOSED</div>
                            <div style={{ color: 'var(--slate-700)' }}>{f.proposed_text}</div>
                          </div>
                        </div>
                      )}
                      {f.type === 'missing_subletter' && f.proposed_subletter && (
                        <div style={{ padding: '8px 10px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 5, fontSize: 11.5, lineHeight: 1.5, marginTop: 6 }}>
                          <div style={{ fontSize: 9.5, fontWeight: 700, color: '#16a34a', marginBottom: 3, letterSpacing: '.04em' }}>PROPOSED NEW SUB-LETTER</div>
                          <div style={{ color: 'var(--slate-700)' }}>{f.proposed_subletter.text}</div>
                        </div>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        <div style={{ padding: '14px 24px', borderTop: '1px solid var(--slate-100)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 12, color: 'var(--slate-500)' }}>
            {findings.length > 0 && !loading
              ? `${checkedCount} of ${findings.length} selected`
              : ''}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onClose} disabled={applying}
              style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid var(--slate-200)', background: 'var(--white)', fontSize: 12.5, cursor: 'pointer', color: 'var(--slate-600)' }}>
              Cancel
            </button>
            <button onClick={onApply} disabled={applying || checkedCount === 0 || loading}
              style={{ padding: '8px 18px', borderRadius: 6, border: 'none',
                background: (applying || checkedCount === 0 || loading) ? 'var(--slate-300)' : 'linear-gradient(135deg,#16a34a,#22c55e)',
                fontSize: 12.5, fontWeight: 700, cursor: (applying || checkedCount === 0 || loading) ? 'not-allowed' : 'pointer', color: '#fff' }}>
              {applying ? 'Applying…' : `Apply ${checkedCount > 0 ? checkedCount + ' ' : ''}Change${checkedCount === 1 ? '' : 's'}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// ExhibitBExtractModal — full-screen overlay for the "Extract from Templates"
// flow. Shows Penny's extracted sections in editable form, lets admin remove
// any they don't want, then saves to doc_template.exhibit_b_template.
//
// This differs from TermReviewModal in shape: rather than findings with
// accept/reject checkboxes, the admin gets a list of sections to review and
// edit wholesale. Each section's heading and text are inline-editable; the
// whole list saves on "Save Template".
// ─────────────────────────────────────────────────────────────────────
function ExhibitBExtractModal({ loading, sections, onUpdate, onRemove, onSave, onClose, saving, exhibit = 'B' }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: 24 }}>
      <div style={{ background: 'var(--white)', borderRadius: 'var(--r-xl, 16px)', width: '100%', maxWidth: 780, maxHeight: '88vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px rgba(0,0,0,0.3)' }}>
        <div style={{ padding: '18px 24px', borderBottom: '1px solid var(--slate-100)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--navy-900)' }}>Exhibit {exhibit} Template — Extracted Sections</div>
            <div style={{ fontSize: 12, color: 'var(--slate-400)', marginTop: 2 }}>
              Review what Penny extracted. Edit any section's heading or text. Remove any you don't want. Save to set the agency baseline.
            </div>
          </div>
          <button onClick={onClose} disabled={saving} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: 'var(--slate-300)' }}>×</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 60, color: 'var(--slate-500)' }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>🔍</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--navy-700)' }}>Penny is reading your Exhibit {exhibit} templates…</div>
              <div style={{ fontSize: 12, color: 'var(--slate-400)', marginTop: 6 }}>Usually takes 20–40 seconds.</div>
            </div>
          ) : !sections || sections.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 60, color: 'var(--slate-500)' }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>No sections extracted</div>
              <div style={{ fontSize: 12, marginTop: 6, color: 'var(--slate-400)' }}>Try again or upload more Exhibit {exhibit} templates first.</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ fontSize: 12, color: 'var(--slate-500)', marginBottom: 4 }}>
                Penny extracted <strong>{sections.length}</strong> section{sections.length === 1 ? '' : 's'}. Edit or remove any before saving.
              </div>
              {sections.map((s, i) => (
                <div key={s.id + '-' + i} style={{
                  padding: 12, background: 'var(--white)',
                  border: '1.5px solid var(--slate-200)', borderRadius: 8,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
                    <input type="text" value={s.heading} onChange={(e) => onUpdate(i, 'heading', e.target.value)}
                      placeholder="Section heading"
                      style={{ flex: 1, padding: '6px 8px', borderRadius: 5, border: '1px solid var(--slate-200)', fontSize: 12, fontWeight: 700, color: 'var(--navy-800)' }} />
                    <button onClick={() => onRemove(i)} title="Remove this section"
                      style={{ padding: '4px 10px', borderRadius: 5, border: '1px solid #fecaca', background: '#fef2f2', fontSize: 11, cursor: 'pointer', color: '#dc2626', fontWeight: 600 }}>
                      Remove
                    </button>
                  </div>
                  <textarea value={s.text} onChange={(e) => onUpdate(i, 'text', e.target.value)}
                    rows={5}
                    style={{ width: '100%', padding: '6px 8px', borderRadius: 5, border: '1px solid var(--slate-200)', fontSize: 12, fontFamily: 'inherit', lineHeight: 1.55, resize: 'vertical' }} />
                </div>
              ))}
              <div style={{ marginTop: 4, padding: 10, background: 'var(--slate-50)', borderRadius: 5, fontSize: 11, color: 'var(--slate-500)', lineHeight: 1.5 }}>
                <strong>Placeholders:</strong> {exhibit === 'B' ? (
                  <><code>{'{contract_total}'}</code>, <code>{'{contractor_name}'}</code>, <code>{'{agreement_num}'}</code>, <code>{'{contract_title}'}</code>, <code>{'{agency_short_name}'}</code>, <code>{'{net_terms_days}'}</code>, <code>{'{invoicing_address}'}</code>, <code>{'{end_date}'}</code>, <code>{'{payment_schedule}'}</code></>
                ) : (
                  <><code>{'{contractor_name}'}</code>, <code>{'{agreement_num}'}</code>, <code>{'{contract_title}'}</code>, <code>{'{agency_short_name}'}</code>, <code>{'{end_date}'}</code></>
                )}. The docx builder substitutes these at draft time.
              </div>
            </div>
          )}
        </div>

        <div style={{ padding: '14px 24px', borderTop: '1px solid var(--slate-100)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} disabled={saving}
            style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid var(--slate-200)', background: 'var(--white)', fontSize: 12.5, cursor: 'pointer', color: 'var(--slate-600)' }}>
            Cancel
          </button>
          <button onClick={onSave} disabled={saving || loading || !sections || sections.length === 0}
            style={{ padding: '8px 18px', borderRadius: 6, border: 'none',
              background: (saving || loading || !sections || sections.length === 0) ? 'var(--slate-300)' : 'linear-gradient(135deg,#16a34a,#22c55e)',
              fontSize: 12.5, fontWeight: 700, cursor: (saving || loading) ? 'not-allowed' : 'pointer', color: '#fff' }}>
            {saving ? 'Saving…' : '💾 Save Template'}
          </button>
        </div>
      </div>
    </div>
  );
}

function UserCard({ user, supervisorName, onAction }) {
  const [showMenu, setShowMenu] = useState(false);
  const isInactive = user.status === 'rejected';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', border: '1px solid var(--slate-200)', borderRadius: 'var(--r-md)', background: isInactive ? 'var(--slate-100)' : 'var(--slate-50)', opacity: isInactive ? 0.7 : 1 }}>
      <div style={{ width: 34, height: 34, borderRadius: '50%', background: isInactive ? 'var(--slate-300)' : 'var(--navy-700)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
        {(user.contact_name || '?').charAt(0).toUpperCase()}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--navy-800)' }}>{user.contact_name}</div>
        <div style={{ fontSize: 11.5, color: 'var(--slate-400)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {user.contact_email}
          {supervisorName ? ' · ' + supervisorName : ''}
          {user.team_name ? ' · ' + user.team_name : ''}
        </div>
      </div>
      <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 99, background: 'var(--navy-50)', color: 'var(--navy-600)', flexShrink: 0 }}>
        {ROLE_LABELS[user.role] || user.role}
      </span>
      {onAction && (
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <button onClick={() => setShowMenu(s => !s)}
            style={{ background: 'none', border: '1px solid var(--slate-200)', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', fontSize: 14, color: 'var(--slate-400)' }}>
            ⋯
          </button>
          {showMenu && (
            <div style={{ position: 'absolute', right: 0, top: '100%', marginTop: 4, background: 'var(--white)', border: '1px solid var(--slate-200)', borderRadius: 'var(--r-md)', boxShadow: '0 8px 24px rgba(0,0,0,.12)', zIndex: 100, minWidth: 160, overflow: 'hidden' }}>
              {!isInactive && (
                <>
                  <button onClick={() => { setShowMenu(false); onAction('role'); }}
                    style={{ display: 'block', width: '100%', padding: '9px 14px', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--navy-700)' }}
                    onMouseEnter={e => e.target.style.background = 'var(--slate-50)'}
                    onMouseLeave={e => e.target.style.background = 'none'}>
                    🎓 Change Role
                  </button>
                  <button onClick={() => { setShowMenu(false); onAction('team'); }}
                    style={{ display: 'block', width: '100%', padding: '9px 14px', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--navy-700)' }}
                    onMouseEnter={e => e.target.style.background = 'var(--slate-50)'}
                    onMouseLeave={e => e.target.style.background = 'none'}>
                    👥 Change Team
                  </button>
                  <div style={{ height: 1, background: 'var(--slate-100)', margin: '2px 0' }} />
                  <button onClick={() => { setShowMenu(false); onAction('deactivate'); }}
                    style={{ display: 'block', width: '100%', padding: '9px 14px', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--red-500)' }}
                    onMouseEnter={e => e.target.style.background = 'var(--red-50)'}
                    onMouseLeave={e => e.target.style.background = 'none'}>
                    🚫 Deactivate
                  </button>
                </>
              )}
              {isInactive && (
                <button onClick={() => { setShowMenu(false); onAction('reinstate'); }}
                  style={{ display: 'block', width: '100%', padding: '9px 14px', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--green-600)' }}>
                  ↺ Reinstate
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PendingUserCard({ user, supervisors, onApprove, onReject, working }) {
  const [selectedSup, setSelectedSup] = useState(user.supervisor_id || '');

  return (
    <div style={{ padding: '14px 16px', border: '1.5px solid var(--orange-200)', borderRadius: 'var(--r-md)', background: 'var(--orange-50)' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
        <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--navy-700)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: 14, fontWeight: 700, flexShrink: 0 }}>
          {(user.contact_name || '?').charAt(0).toUpperCase()}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--navy-800)' }}>{user.contact_name || 'Unknown'}</div>
          <div style={{ fontSize: 12, color: 'var(--slate-500)' }}>{user.contact_email}</div>
          <div style={{ fontSize: 11, color: 'var(--slate-400)', marginTop: 2 }}>
            Requested {user.created_at ? new Date(user.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
            {user.supervisor_id && ' · Requested supervisor: ' + (supervisors.find(s => s.id === user.supervisor_id)?.contact_name || 'Unknown')}
          </div>
        </div>
      </div>

      {/* Supervisor assignment */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--slate-500)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 5 }}>
          Assign to Team
        </div>
        <select value={selectedSup} onChange={e => setSelectedSup(e.target.value)}
          style={{ width: '100%', padding: '7px 10px', borderRadius: 'var(--r-md)', border: '1.5px solid var(--orange-200)', fontSize: 13, background: 'var(--white)' }}>
          <option value="">-- Unassigned (add to team later) --</option>
          {supervisors.filter(s => ['admin','agency_super_admin'].includes(s.role) || s.id).map(s => (
            <option key={s.id} value={s.id}>{s.contact_name}{s.team_name ? ' — ' + s.team_name : ''}</option>
          ))}
        </select>
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn btn-gold" style={{ flex: 1, justifyContent: 'center' }}
          disabled={working} onClick={() => onApprove(user.id, user.contact_name, selectedSup)}>
          {working ? '…' : '✓ Approve'}
        </button>
        <button className="btn btn-ghost" style={{ color: 'var(--red-400)' }}
          disabled={working} onClick={() => onReject(user.id, user.contact_name)}>
          ✕ Reject
        </button>
      </div>
    </div>
  );
}

function UserActionModal({ action, user, analysts, supervisors, onChangeRole, onChangeTeam, onDeactivate, onReinstate, onClose, working }) {
  const [selectedValue, setSelectedValue] = useState('');
  const [reassignContracts, setReassignContracts] = useState('');

  const titles = { role: 'Change Role', team: 'Change Team', deactivate: 'Deactivate User', reinstate: 'Reinstate User' };
  const roleOptions = [
    { value: 'member', label: 'Analyst' },
    { value: 'admin', label: 'Supervisor' },
    { value: 'agency_super_admin', label: 'Division Chief' },
  ].filter(r => r.value !== user.role);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: 24 }}>
      <div style={{ background: 'var(--white)', borderRadius: 'var(--r-lg)', padding: '28px 32px', maxWidth: 460, width: '100%', boxShadow: '0 24px 64px rgba(0,0,0,.3)' }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy-900)', marginBottom: 6 }}>{titles[action]}</h3>
        <p style={{ fontSize: 13, color: 'var(--slate-500)', marginBottom: 20, lineHeight: 1.6 }}>
          <strong>{user.contact_name}</strong> · {user.contact_email}
        </p>

        {/* Change Role */}
        {action === 'role' && (
          <div style={{ marginBottom: 20 }}>
            <label style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--slate-500)', display: 'block', marginBottom: 6 }}>New Role</label>
            <select value={selectedValue} onChange={e => setSelectedValue(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', borderRadius: 'var(--r-md)', border: '1.5px solid var(--slate-200)', fontSize: 13 }}>
              <option value="">-- Select new role --</option>
              {roleOptions.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            {selectedValue === 'agency_super_admin' && (
              <div style={{ marginTop: 8, padding: '8px 12px', background: 'var(--orange-50)', borderRadius: 'var(--r-md)', fontSize: 12, color: 'var(--orange-700)' }}>
                ⚠ This will give them full agency visibility and management access.
              </div>
            )}
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button className="btn btn-gold" style={{ flex: 1, justifyContent: 'center' }} disabled={!selectedValue || working}
                onClick={() => onChangeRole(user.id, user.contact_name, selectedValue)}>
                {working ? '…' : 'Update Role'}
              </button>
              <button className="btn btn-ghost" style={{ flex: 1, justifyContent: 'center' }} onClick={onClose}>Cancel</button>
            </div>
          </div>
        )}

        {/* Change Team */}
        {action === 'team' && (
          <div style={{ marginBottom: 20 }}>
            <label style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--slate-500)', display: 'block', marginBottom: 6 }}>Assign to Supervisor</label>
            <select value={selectedValue} onChange={e => setSelectedValue(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', borderRadius: 'var(--r-md)', border: '1.5px solid var(--slate-200)', fontSize: 13 }}>
              <option value="">-- Unassigned --</option>
              {supervisors.map(s => <option key={s.id} value={s.id}>{s.contact_name}{s.team_name ? ' (' + s.team_name + ')' : ''}</option>)}
            </select>
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button className="btn btn-gold" style={{ flex: 1, justifyContent: 'center' }} disabled={working}
                onClick={() => onChangeTeam(user.id, user.contact_name, selectedValue)}>
                {working ? '…' : 'Move to Team'}
              </button>
              <button className="btn btn-ghost" style={{ flex: 1, justifyContent: 'center' }} onClick={onClose}>Cancel</button>
            </div>
          </div>
        )}

        {/* Deactivate */}
        {action === 'deactivate' && (
          <div>
            <div style={{ padding: '12px 14px', background: 'var(--red-50)', border: '1px solid var(--red-200)', borderRadius: 'var(--r-md)', fontSize: 13, color: 'var(--red-700)', marginBottom: 16, lineHeight: 1.6 }}>
              ⚠ Deactivating this user will immediately revoke their access. Their contracts and team members can be reassigned below.
            </div>
            <label style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--slate-500)', display: 'block', marginBottom: 6 }}>
              Reassign contracts & team to <span style={{ fontWeight: 400, textTransform: 'none' }}>(optional but recommended)</span>
            </label>
            <select value={reassignContracts} onChange={e => setReassignContracts(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', borderRadius: 'var(--r-md)', border: '1.5px solid var(--slate-200)', fontSize: 13, marginBottom: 16 }}>
              <option value="">-- Do not reassign --</option>
              {[...analysts, ...supervisors].filter((u, i, arr) => arr.findIndex(x => x.id === u.id) === i)
                .map(a => <option key={a.id} value={a.id}>{a.contact_name}</option>)}
            </select>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-ghost" style={{ flex: 1, justifyContent: 'center', color: 'var(--red-500)', borderColor: 'var(--red-200)' }} disabled={working}
                onClick={() => onDeactivate(user.id, user.contact_name, reassignContracts)}>
                {working ? '…' : '🚫 Deactivate'}
              </button>
              <button className="btn btn-ghost" style={{ flex: 1, justifyContent: 'center' }} onClick={onClose}>Cancel</button>
            </div>
          </div>
        )}

        {/* Reinstate */}
        {action === 'reinstate' && (
          <div>
            <p style={{ fontSize: 13, color: 'var(--slate-500)', marginBottom: 20, lineHeight: 1.6 }}>
              Reinstating this user will restore their access to GoldenScope. Their existing contracts and data are intact.
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-gold" style={{ flex: 1, justifyContent: 'center' }} disabled={working}
                onClick={() => onReinstate(user.id, user.contact_name)}>
                {working ? '…' : '↺ Reinstate'}
              </button>
              <button className="btn btn-ghost" style={{ flex: 1, justifyContent: 'center' }} onClick={onClose}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ReassignModal({ contract, analysts, onReassign, onClose }) {
  const [selectedAnalyst, setSelectedAnalyst] = useState('');
  const selected = analysts.find(a => a.id === selectedAnalyst);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: 24 }}>
      <div style={{ background: 'var(--white)', borderRadius: 'var(--r-lg)', padding: '28px 32px', maxWidth: 460, width: '100%', boxShadow: '0 24px 64px rgba(0,0,0,.3)' }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy-900)', marginBottom: 6 }}>Reassign Contract</h3>
        <p style={{ fontSize: 13, color: 'var(--slate-500)', marginBottom: 16, lineHeight: 1.6 }}>
          <strong>{contract.title || 'Untitled'}</strong> is currently assigned to <strong>{contract.analystName || 'Unknown'}</strong>. All contract data, drafts, and history will transfer to the new analyst.
        </p>
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--slate-500)', display: 'block', marginBottom: 6 }}>
            Reassign to
          </label>
          <select value={selectedAnalyst} onChange={e => setSelectedAnalyst(e.target.value)}
            style={{ width: '100%', padding: '10px 12px', borderRadius: 'var(--r-md)', border: '1.5px solid var(--slate-200)', fontSize: 13 }}>
            <option value="">-- Select analyst --</option>
            {analysts.map(a => (
              <option key={a.id} value={a.id}>{a.contact_name}{a.team_name ? ' (' + a.team_name + ')' : ''}</option>
            ))}
          </select>
        </div>
        {selected && (
          <div style={{ padding: '10px 12px', background: 'var(--teal-50)', border: '1px solid var(--teal-200)', borderRadius: 'var(--r-md)', fontSize: 12.5, color: 'var(--teal-700)', marginBottom: 16 }}>
            ✓ Contract will be transferred to <strong>{selected.contact_name}</strong>. They will see it on their dashboard immediately.
          </div>
        )}
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-gold" style={{ flex: 1, justifyContent: 'center' }}
            disabled={!selectedAnalyst}
            onClick={() => onReassign(contract.id, selectedAnalyst, selected?.contact_name)}>
            Reassign Contract
          </button>
          <button className="btn btn-ghost" style={{ flex: 1, justifyContent: 'center' }} onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// PhraseChips — small inline chip-style multi-input.
//
// Used in the Doc Profile form to manage drafting_voice.preferred_phrases
// and drafting_voice.avoid_phrases. Renders the current array as removable
// chips plus a single text input. Pressing Enter or comma adds the typed
// value to the array; clicking × on a chip removes it.
//
// Props:
//   label       — heading shown above the chips (e.g. "Avoid these words")
//   placeholder — placeholder text for the input
//   values      — string[] (current array)
//   onChange    — fn(next: string[]) — called with the new array on add/remove
//   accent      — chip text color (hex)
//   accentBg    — chip background color (hex)
//   accentBorder— chip border color (hex)
// ─────────────────────────────────────────────────────────────────────
function PhraseChips({ label, placeholder, values, onChange, accent, accentBg, accentBorder }) {
  const [draft, setDraft] = useState('');

  function commit(raw) {
    const cleaned = String(raw || '').trim();
    if (!cleaned) return;
    // Allow comma-separated input — if the user types "will, must" and presses
    // Enter, both get added as separate chips.
    const parts = cleaned.split(',').map(s => s.trim()).filter(Boolean);
    const existing = new Set((values || []).map(v => v.toLowerCase()));
    const additions = parts.filter(p => !existing.has(p.toLowerCase()));
    if (additions.length > 0) onChange([...(values || []), ...additions]);
    setDraft('');
  }

  function remove(idx) {
    const next = [...(values || [])];
    next.splice(idx, 1);
    onChange(next);
  }

  function onKeyDown(e) {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commit(draft);
    } else if (e.key === 'Backspace' && !draft && (values || []).length > 0) {
      // Backspace on empty input removes the last chip — common UX pattern
      remove((values || []).length - 1);
    }
  }

  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--slate-600)', marginBottom: 6 }}>
        {label}
      </div>
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center',
        padding: 6, borderRadius: 6, border: '1.5px solid var(--slate-200)', background: '#fff',
        minHeight: 38,
      }}>
        {(values || []).map((v, i) => (
          <span key={`${v}-${i}`} style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '3px 4px 3px 10px', borderRadius: 12,
            background: accentBg, border: `1px solid ${accentBorder}`,
            color: accent, fontSize: 12, fontWeight: 600,
          }}>
            {v}
            <button onClick={() => remove(i)}
              title={`Remove "${v}"`}
              style={{
                background: 'transparent', border: 'none', cursor: 'pointer',
                color: accent, fontSize: 14, lineHeight: 1, padding: '0 4px',
                fontWeight: 700,
              }}>
              ×
            </button>
          </span>
        ))}
        <input
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={() => { if (draft) commit(draft); }}
          placeholder={(values || []).length === 0 ? placeholder : ''}
          style={{
            flex: 1, minWidth: 80,
            border: 'none', outline: 'none', background: 'transparent',
            fontSize: 13, padding: '4px 6px',
          }}
        />
      </div>
    </div>
  );
}
